import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Card } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import { formatCredentials } from "../utils/formatCredentials";
import axios from "axios";
import { getConfigWithToken } from "../config/config";
import { formatDateOnly } from "../utils/formatDateOnly";
import styles from './SessionHistoryDetailView.module.css';
import SessionDetailedViewPage from './SessionDetailedViewPage';
import AddendumView from './AddendumView';
import SessionHistoryPDFGenerator from './SessionHistoryPDFGenerator';
import SessionActionButtons from '../components/SessionActionButtons';
import SignAndLockCard from '../components/SignAndLockCard';
import AppointmentDetailsSection from './sessionHistorySections/AppointmentDetailsSection';
import PresentingConcernsSection from './sessionHistorySections/PresentingConcernsSection';
import HistoryConcernsSection from './sessionHistorySections/HistoryConcernsSection';
import MedicalConcernsSection from './sessionHistorySections/MedicalConcernsSection';
import SubstanceConcernsSection from './sessionHistorySections/SubstanceConcernsSection';
import FamilyConcernsSection from './sessionHistorySections/FamilyConcernsSection';
import EducationConcernsSection from './sessionHistorySections/EducationConcernsSection';
import MentalStatusConcernsSection from './sessionHistorySections/MentalStatusConcernsSection';
import RiskSafetyConcernsSection from './sessionHistorySections/RiskSafetyConcernsSection';
import StrengthConcernsSection from './sessionHistorySections/StrengthConcernsSection';
import DiagnosisConcernsSection from './sessionHistorySections/DiagnosisConcernsSection';
import CarePlanConcernsSection from './sessionHistorySections/CarePlanConcernsSection';

const SessionHistoryDetailView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, openAddendum, editExistingAddendum } = location.state || {};
  const backPath = location.state?.from || null;

  const handleBackNavigation = useCallback(() => {
    if (backPath) {
      navigate(backPath, { replace: true });
      return;
    }

    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/session-history");
    }
  }, [navigate, backPath]);
  const user = useSelector((state) => state?.user);
  const [activeSection, setActiveSection] = useState("appointment-details");
  const [loading, setLoading] = useState(true);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isSigned, setIsSigned] = useState(false);
  const [isSignedFromDB, setIsSignedFromDB] = useState(false); // Track isSigned from database
  const [saving, setSaving] = useState(false);
  const [signingAndSubmitting, setSigningAndSubmitting] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSavedTimestamp, setLastSavedTimestamp] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState("Save");
  const [retryTimeoutId, setRetryTimeoutId] = useState(null);
  const [lastSavedData, setLastSavedData] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const errorToastShownRef = useRef(false);
  const [checkboxError, setCheckboxError] = useState("");
  const [durationError, setDurationError] = useState("");
  const [frequencyError, setFrequencyError] = useState("");
  const [severityError, setSeverityError] = useState("");
  const [alcoholError, setAlcoholError] = useState("");
  const [cannabisError, setCannabisError] = useState("");
  const [tobaccoError, setTobaccoError] = useState("");
  const [illicitDrugsError, setIllicitDrugsError] = useState("");
  const [livingSituationError, setLivingSituationError] = useState("");
  const [supportSystemError, setSupportSystemError] = useState("");
  const [medicalConditionsError, setMedicalConditionsError] = useState("");
  const [educationLevelError, setEducationLevelError] = useState("");
  const [employmentStatusError, setEmploymentStatusError] = useState("");
  const [appearanceError, setAppearanceError] = useState("");
  const [behaviorError, setBehaviorError] = useState("");
  const [speechError, setSpeechError] = useState("");
  const [moodError, setMoodError] = useState("");
  const [affectError, setAffectError] = useState("");
  const [thoughtProcessError, setThoughtProcessError] = useState("");
  const [orientationError, setOrientationError] = useState("");
  const [insightJudgmentError, setInsightJudgmentError] = useState("");
  const [suicideRiskError, setSuicideRiskError] = useState("");
  const [homicideRiskError, setHomicideRiskError] = useState("");
  const [abuseNeglectError, setAbuseNeglectError] = useState("");
  const [safetyPlanNotesError, setSafetyPlanNotesError] = useState("");
  const [strengthsError, setStrengthsError] = useState("");
  const [clientGoalError, setClientGoalError] = useState("");
  const [objectivesError, setObjectivesError] = useState("");
  const [interventionsError, setInterventionsError] = useState("");
  const [sessionFrequencyError, setSessionFrequencyError] = useState("");
  const [reviewDateError, setReviewDateError] = useState("");
  const [primaryDiagnosisError, setPrimaryDiagnosisError] = useState("");
  const [addendumTextError, setAddendumTextError] = useState("");

  const [primaryDiagnosisSearchTerm, setPrimaryDiagnosisSearchTerm] = useState("");
  const [primaryDiagnosisResults, setPrimaryDiagnosisResults] = useState([]);
  const [diagnosisSearchLoading, setDiagnosisSearchLoading] = useState(false);
  const [diagnosisSearchError, setDiagnosisSearchError] = useState("");
  const [isPrimaryDiagnosisDropdownOpen, setIsPrimaryDiagnosisDropdownOpen] = useState(false);
  const diagnosisCodeRef = useRef(null);

  const [secondaryDiagnosisSearchTerm, setSecondaryDiagnosisSearchTerm] = useState("");
  const [secondaryDiagnosisResults, setSecondaryDiagnosisResults] = useState([]);
  const [secondaryDiagnosisSearchLoading, setSecondaryDiagnosisSearchLoading] = useState(false);
  const [secondaryDiagnosisSearchError, setSecondaryDiagnosisSearchError] = useState("");
  const [isSecondaryDiagnosisDropdownOpen, setIsSecondaryDiagnosisDropdownOpen] = useState(false);
  const secondaryDiagnosisCodeRef = useRef(null);

  const [tertiaryDiagnosisSearchTerm, setTertiaryDiagnosisSearchTerm] = useState("");
  const [tertiaryDiagnosisResults, setTertiaryDiagnosisResults] = useState([]);
  const [tertiaryDiagnosisSearchLoading, setTertiaryDiagnosisSearchLoading] = useState(false);
  const [tertiaryDiagnosisSearchError, setTertiaryDiagnosisSearchError] = useState("");
  const [isTertiaryDiagnosisDropdownOpen, setIsTertiaryDiagnosisDropdownOpen] = useState(false);
  const tertiaryDiagnosisCodeRef = useRef(null);

  const [isTimeInPickerOpen, setIsTimeInPickerOpen] = useState(false);
  const [timeInParts, setTimeInParts] = useState({ hour: "", minute: "", period: "" });
  const timeInPickerRef = useRef(null);
  const timeInUpdateFromInputRef = useRef(false);

  const [isTimeOutPickerOpen, setIsTimeOutPickerOpen] = useState(false);
  const [timeOutParts, setTimeOutParts] = useState({ hour: "", minute: "", period: "" });
  const timeOutPickerRef = useRef(null);
  const timeOutUpdateFromInputRef = useRef(false);
  const [addAddendum, setAddAddendum] = useState(false);
  const [addendumData, setAddendumData] = useState([]);
  const [currentAddendumIndex, setCurrentAddendumIndex] = useState(null); // Track which addendum we're editing
  const addendumInitializedRef = useRef(false); // Track if addendum textarea has been initialized

  // Helper function to process addendumData - ensures addendumSignedAt is present in every entry
  // By default null, and only updates to timestamp when addendumSigned becomes true
  // Once addendumSigned is true, the entry is locked and cannot be modified
  const processAddendumData = useCallback((data) => {
    if (!data || !Array.isArray(data)) {
      return data || [];
    }
    return data.map((entry) => {
      // Agar addendumSigned true hai to entry ko lock kar do - koi modification nahi
      // Entry ko exactly as-is preserve karo (signed entries are immutable)
      if (entry.addendumSigned === true) {
        return entry; // Locked entry - no changes allowed
      }

      // Agar addendumSigned false hai to addendumSignedAt ko null ensure karo
      return {
        ...entry,
        addendumSignedAt: null
      };
    });
  }, []);

  const handleSubstanceTextareaChange = (element, updateFunction, value) => {
    if (!element) return;
    updateFunction(value);

    // Simple: if empty, hide scrollbar. Otherwise check overflow
    setTimeout(() => {
      if (!value || value.trim() === "") {
        element.style.overflowY = "hidden";
      } else {
        const hasOverflow = element.scrollHeight > element.clientHeight;
        element.style.overflowY = hasOverflow ? "auto" : "hidden";
      }
    }, 0);
  };

  const autoResizeTextarea = (element, respectMinHeight = false) => {
    if (!element) return;
    if (respectMinHeight) {
      // Get the minimum height from inline style
      let minHeight = 72; // default
      if (element.style.minHeight) {
        const minHeightStr = element.style.minHeight;
        minHeight = parseInt(minHeightStr) || 72;
      }

      // Check if value is empty or just whitespace
      const hasValue = element.value && element.value.trim().length > 0;

      if (!hasValue) {
        // If empty, set to fixed minHeight
        element.style.height = `${minHeight}px`;
        return;
      }

      // Function to calculate and set height
      const calculateHeight = () => {
        // Reset height to auto first to allow proper recalculation
        element.style.height = "auto";
        // Force a reflow to ensure scrollHeight is calculated correctly
        void element.offsetHeight;

        // Get the actual scrollHeight needed for the content
        const scrollHeight = element.scrollHeight;
        // Calculate new height, ensuring it's at least minHeight
        const newHeight = Math.max(minHeight, scrollHeight);
        // Set the new height
        element.style.height = `${newHeight}px`;
      };

      // Calculate immediately
      calculateHeight();

      // Use requestAnimationFrame to ensure height updates for Enter key presses
      // This handles cases where DOM update happens slightly after onChange
      requestAnimationFrame(() => {
        if (element && element.value) {
          calculateHeight();
        }
      });
    } else {
      // Original simple implementation for backward compatibility
      element.style.height = "auto";
      element.style.height = `${element.scrollHeight}px`;
    }
  };

  const handleTimeSegmentBlur = (fieldName, setParts, segment) => {
    setParts((prev) => {
      const updated = { ...prev };
      const formattedParts = { ...updated };
      if (segment === "hour" && formattedParts.hour) {
        if (formattedParts.hour.length === 1) {
          // Single digit handling
          if (formattedParts.hour === "0") {
            // If only "0" is entered, convert to "01" on blur
            formattedParts.hour = "01";
          } else if (formattedParts.hour === "1") {
            // Keep "1" as is (no padding)
            formattedParts.hour = "1";
          } else {
            // For 2-9, pad with 0
            const hourNum = parseInt(formattedParts.hour, 10);
            if (hourNum >= 2 && hourNum <= 9) {
              formattedParts.hour = formattedParts.hour.padStart(2, "0");
            } else {
              formattedParts.hour = "1";
            }
          }
        } else if (formattedParts.hour === "00") {
          // Convert 00 to 12
          formattedParts.hour = "12";
        } else if (formattedParts.hour.length === 2) {
          // Validate two-digit hour
          const hourNum = parseInt(formattedParts.hour, 10);
          if (hourNum < 1 || hourNum > 12) {
            // If invalid, convert to valid range
            if (hourNum === 0) {
              formattedParts.hour = "12";
            } else if (hourNum > 12) {
              formattedParts.hour = "12";
            } else {
              formattedParts.hour = "1";
            }
          } else if (hourNum === 1) {
            // If it's "01", keep it. If it's "10", "11", "12", keep with padding
            formattedParts.hour = formattedParts.hour;
          }
        }
      }
      if (segment === "minute" && formattedParts.minute && formattedParts.minute.length === 1) {
        formattedParts.minute = formattedParts.minute.padStart(2, "0");
      }
      const formatted = formatTimeFromParts(formattedParts);
      if (formatted) {
        markTimeFieldUpdate(fieldName);
        updateFormData(fieldName, formatted);
      }
      return formattedParts;
    });
  };

  const [sessionFormData, setSessionFormData] = useState({
    reasonForSeekingServices: "",
    selectedConcerns: [],
    otherConcernText: "",
    duration: "",
    frequency: "",
    severity: "",
    freeTextHistory: "",

    selectedMedicalConditions: [],
    otherMedicalConditionText: "",
    selectedPastPsychiatric: [],
    otherPastPsychiatricText: "",
    currentMedications: "",
    selectedFamilyPsychiatric: [],
    otherFamilyPsychiatricText: "",

    alcohol: "",
    alcoholDetails: "",
    cannabis: "",
    cannabisDetails: "",
    tobacco: "",
    tobaccoDetails: "",
    illicitDrugs: "",
    illicitDrugsDetails: "",
    substanceUseDetails: "",

    livingSituation: "",
    selectedSupportSystem: [],
    otherSupportSystemText: "",
    familySocialNotes: "",

    highestEducationLevel: "",
    otherEducationLevelText: "",
    employmentStatus: "",
    otherEmploymentStatusText: "",
    currentJobRole: "",

    appearance: "",
    behavior: "",
    speech: "",
    mood: "",
    affect: "",
    thoughtProcess: "",
    orientation: "",
    insightJudgment: "",

    suicideRisk: "",
    homicideRisk: "",
    abuseNeglect: "",
    safetyPlanNotes: "",

    selectedStrengths: [],
    otherStrengthsText: "",
    clientStrengthsDescription: "",

    primaryDiagnosis: "",
    primaryDiagnosisDescription: "",
    secondaryDiagnosis: "",
    secondaryDiagnosisDescription: "",
    tertiaryDiagnosis: "",
    tertiaryDiagnosisDescription: "",

    clientGoal: "",
    objectives: "",
    interventions: "",
    sessionFrequency: "",
    otherSessionFrequencyText: "",
    reviewDate: "",
    timeIn: "",
    timeOut: "",
    wasClientAtHome: "",
    isSigned: false,
  });

  const updateFormData = (fieldName, value) => {
    setSessionFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    setHasUnsavedChanges(true);
  };

  const getDefaultTimeParts = () => {
    const now = new Date();
    let hours = now.getHours();
    const period = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return {
      hour: hours.toString().padStart(2, "0"),
      minute: now.getMinutes().toString().padStart(2, "0"),
      period
    };
  };

  const formatTimeFromParts = (parts) => {
    if (!parts.hour || !parts.minute || !parts.period) {
      return "";
    }
    const hourNumber = parseInt(parts.hour, 10);
    if (Number.isNaN(hourNumber) || hourNumber < 1 || hourNumber > 12) {
      return "";
    }
    const minuteValue = parts.minute.padStart(2, "0");
    const periodValue = parts.period.toUpperCase();
    const hourDisplay = hourNumber.toString().padStart(2, "0");
    return `${hourDisplay}:${minuteValue} ${periodValue}`;
  };

  const parseTimeString = useCallback((value = "") => {
    if (!value) return null;
    const trimmedValue = value.trim().toUpperCase();
    const match = trimmedValue.match(/^(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM)$/);
    if (!match) return null;
    let hourNumber = parseInt(match[1], 10);
    if (Number.isNaN(hourNumber) || hourNumber < 1) {
      hourNumber = 1;
    } else if (hourNumber > 12) {
      hourNumber = 12;
    }
    let minuteNumber = match[2] !== undefined ? parseInt(match[2], 10) : 0;
    if (Number.isNaN(minuteNumber) || minuteNumber < 0) {
      minuteNumber = 0;
    } else if (minuteNumber > 59) {
      minuteNumber = 59;
    }
    return {
      hour: hourNumber.toString().padStart(2, "0"),
      minute: minuteNumber.toString().padStart(2, "0"),
      period: match[3]
    };
  }, []);

  const getMinutesFromTimeString = useCallback(
    (value) => {
      const parsed = parseTimeString(value);
      if (!parsed) return null;

      const hour = parseInt(parsed.hour, 10);
      const minute = parseInt(parsed.minute, 10);
      if (Number.isNaN(hour) || Number.isNaN(minute)) {
        return null;
      }

      let hour24 = hour % 12;
      if (parsed.period === "PM") {
        hour24 += 12;
      } else if (parsed.period === "AM" && hour === 12) {
        hour24 = 0;
      }

      return hour24 * 60 + minute;
    },
    [parseTimeString]
  );

  const ensureTimeParts = (parts) => {
    const defaults = getDefaultTimeParts();
    return {
      hour: parts.hour || defaults.hour,
      minute: parts.minute || defaults.minute,
      period: parts.period || defaults.period
    };
  };

  const markTimeFieldUpdate = (fieldName) => {
    if (fieldName === "timeIn") {
      timeInUpdateFromInputRef.current = true;
    } else if (fieldName === "timeOut") {
      timeOutUpdateFromInputRef.current = true;
    }
  };

  const handleTimeSegmentChange = (fieldName, setParts, segment, value) => {
    setParts((prev) => {
      const updated = { ...prev };
      if (segment === "hour") {
        if (!value) {
          updated.hour = "";
        } else {
          const digits = value.replace(/\D/g, "");
          if (!digits) {
            updated.hour = "";
          } else if (digits.length === 1) {
            const firstDigit = digits[0];
            if (firstDigit === "0") {
              updated.hour = "0";
            } else if (firstDigit === "1") {
              updated.hour = "1";
            } else {
              const hourNumber = parseInt(firstDigit, 10);
              if (hourNumber >= 2 && hourNumber <= 9) {
                updated.hour = hourNumber.toString().padStart(2, "0");
              } else {
                updated.hour = "01";
              }
            }
          } else {
            const normalizedDigits = digits.slice(-2);
            let hourNumber = parseInt(normalizedDigits, 10);
            if (normalizedDigits === "00") {
              hourNumber = 12;
              updated.hour = "12";
            } else if (Number.isNaN(hourNumber)) {
              updated.hour = "1";
            } else if (hourNumber < 1) {
              updated.hour = "1";
            } else if (hourNumber > 12) {
              updated.hour = "12";
            } else {
              if (normalizedDigits[0] === "0") {
                const secondDigit = parseInt(normalizedDigits[1], 10);
                if (secondDigit >= 1 && secondDigit <= 9) {
                  updated.hour = normalizedDigits;
                } else {
                  updated.hour = prev.hour || "1";
                }
              } else if (hourNumber === 1) {
                updated.hour = hourNumber.toString().padStart(2, "0");
              } else {
                updated.hour = hourNumber.toString().padStart(2, "0");
              }
            }
          }
        }
      } else if (segment === "minute") {
        if (!value) {
          updated.minute = "";
        } else {
          const digits = value.replace(/\D/g, "");
          if (!digits) {
            updated.minute = "";
          } else if (digits.length === 1) {
            updated.minute = digits;
          } else {
            const normalizedDigits = digits.slice(-2);
            let minuteNumber = parseInt(normalizedDigits, 10);
            if (Number.isNaN(minuteNumber) || minuteNumber < 0) {
              minuteNumber = 0;
            } else if (minuteNumber > 59) {
              minuteNumber = 59;
            }
            updated.minute = minuteNumber.toString().padStart(2, "0");
          }
        }
      } else if (segment === "period") {
        if (!value) {
          updated.period = "";
        } else {
          const letters = value.replace(/[^aApPmM]/g, "").toUpperCase();
          if (!letters) {
            updated.period = "";
          } else if (letters.startsWith("A")) {
            updated.period = "AM";
          } else if (letters.startsWith("P")) {
            updated.period = "PM";
          }
        }
      }
      const formatted = formatTimeFromParts(updated);
      if (formatted) {
        markTimeFieldUpdate(fieldName);
        updateFormData(fieldName, formatted);
      } else if (!updated.hour && !updated.minute && !updated.period) {
        markTimeFieldUpdate(fieldName);
        updateFormData(fieldName, "");
      }
      return updated;
    });
  };

  const handleTimeInputFocus = (setPickerOpen) => {
    setPickerOpen(true);
  };

  const handleHourAdjust = (fieldName, setParts, direction) => {
    setParts((prev) => {
      const base = ensureTimeParts(prev);
      let hourValue = parseInt(base.hour, 10);
      if (Number.isNaN(hourValue) || hourValue < 1 || hourValue > 12) {
        hourValue = 12;
      }
      hourValue = ((hourValue - 1 + direction + 12) % 12) + 1;
      const updated = { ...base, hour: hourValue.toString().padStart(2, "0") };
      markTimeFieldUpdate(fieldName);
      updateFormData(fieldName, formatTimeFromParts(updated));
      return updated;
    });
  };

  const handleMinuteAdjust = (fieldName, setParts, direction) => {
    setParts((prev) => {
      const base = ensureTimeParts(prev);
      let minuteValue = parseInt(base.minute, 10);
      if (Number.isNaN(minuteValue) || minuteValue < 0 || minuteValue > 59) {
        minuteValue = 0;
      }
      minuteValue = (minuteValue + direction + 60) % 60;
      const updated = { ...base, minute: minuteValue.toString().padStart(2, "0") };
      markTimeFieldUpdate(fieldName);
      updateFormData(fieldName, formatTimeFromParts(updated));
      return updated;
    });
  };

  const handlePeriodToggle = (fieldName, setParts) => {
    setParts((prev) => {
      const base = ensureTimeParts(prev);
      const updated = { ...base, period: base.period === "AM" ? "PM" : "AM" };
      markTimeFieldUpdate(fieldName);
      updateFormData(fieldName, formatTimeFromParts(updated));
      return updated;
    });
  };

  useEffect(() => {
    if (session?._id) {
      fetchSessionHistoryDetail();
    }
  }, [session]);

  useEffect(() => {
    if (timeInUpdateFromInputRef.current) {
      timeInUpdateFromInputRef.current = false;
      return;
    }
    if (!sessionFormData.timeIn) {
      setTimeInParts({ hour: "", minute: "", period: "" });
      return;
    }
    const parsed = parseTimeString(sessionFormData.timeIn);
    if (parsed) {
      setTimeInParts(parsed);
    }
  }, [sessionFormData.timeIn, parseTimeString]);

  useEffect(() => {
    if (!isTimeInPickerOpen) return;
    const handleClickOutside = (event) => {
      if (timeInPickerRef.current && !timeInPickerRef.current.contains(event.target)) {
        setIsTimeInPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isTimeInPickerOpen]);

  useEffect(() => {
    if (timeOutUpdateFromInputRef.current) {
      timeOutUpdateFromInputRef.current = false;
      return;
    }
    if (!sessionFormData.timeOut) {
      setTimeOutParts({ hour: "", minute: "", period: "" });
      return;
    }
    const parsed = parseTimeString(sessionFormData.timeOut);
    if (parsed) {
      setTimeOutParts(parsed);
    }
  }, [sessionFormData.timeOut, parseTimeString]);

  useEffect(() => {
    if (!isTimeOutPickerOpen) return;
    const handleClickOutside = (event) => {
      if (timeOutPickerRef.current && !timeOutPickerRef.current.contains(event.target)) {
        setIsTimeOutPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isTimeOutPickerOpen]);

  useEffect(() => {
    if (sessionFormData.selectedConcerns.includes("Other")) {
      autoResizeTextarea(document.getElementById("concern-other-textarea"));
    }
  }, [sessionFormData.selectedConcerns, sessionFormData.otherConcernText]);

  useEffect(() => {
    if (sessionFormData.selectedMedicalConditions.includes("Other")) {
      autoResizeTextarea(document.getElementById("medical-other-textarea"));
    }
  }, [sessionFormData.selectedMedicalConditions, sessionFormData.otherMedicalConditionText]);

  useEffect(() => {
    if (sessionFormData.selectedPastPsychiatric.includes("Other")) {
      autoResizeTextarea(document.getElementById("past-psychiatric-other-textarea"));
    }
  }, [sessionFormData.selectedPastPsychiatric, sessionFormData.otherPastPsychiatricText]);

  useEffect(() => {
    if (sessionFormData.selectedFamilyPsychiatric.includes("Other")) {
      autoResizeTextarea(document.getElementById("family-psychiatric-other-textarea"));
    }
  }, [sessionFormData.selectedFamilyPsychiatric, sessionFormData.otherFamilyPsychiatricText]);

  useEffect(() => {
    if (sessionFormData.selectedSupportSystem.includes("Other")) {
      autoResizeTextarea(document.getElementById("support-system-other-textarea"));
    }
  }, [sessionFormData.selectedSupportSystem, sessionFormData.otherSupportSystemText]);

  useEffect(() => {
    if (sessionFormData.highestEducationLevel === "Other") {
      autoResizeTextarea(document.getElementById("other-Education-Level-Text"));
    }
  }, [sessionFormData.highestEducationLevel, sessionFormData.otherEducationLevelText]);

  useEffect(() => {
    if (sessionFormData.employmentStatus === "Other") {
      autoResizeTextarea(document.getElementById("other-Employment-Status-Text"));
    }
  }, [sessionFormData.employmentStatus, sessionFormData.otherEmploymentStatusText]);

  useEffect(() => {
    if (sessionFormData.selectedStrengths.includes("Other")) {
      autoResizeTextarea(document.getElementById("other-Strength-Text"));
    }
  }, [sessionFormData.selectedStrengths, sessionFormData.otherStrengthsText]);

  useEffect(() => {
    if (sessionFormData.sessionFrequency === "Other") {
      autoResizeTextarea(document.getElementById("other-Session-Frequency-Text"));
    }
  }, [sessionFormData.sessionFrequency, sessionFormData.otherSessionFrequencyText]);

  const fetchPrimaryDiagnosisResults = useCallback(async (query = "") => {
    setDiagnosisSearchLoading(true);
    setDiagnosisSearchError("");
    try {
      const response = await axios.get("https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search", {
        params: {
          sf: "code,name",
          df: "code,name",
          terms: query || "",
          maxList: 25,
        },
      });
      const data = response.data;
      if (Array.isArray(data) && Array.isArray(data[3])) {
        const formatted = data[3].map((item) => ({
          code: item[0],
          description: item[1],
        }));
        setPrimaryDiagnosisResults(formatted);
      } else {
        setPrimaryDiagnosisResults([]);
      }
    } catch (error) {
      setDiagnosisSearchError("Unable to fetch diagnosis codes. Please try again.");
      setPrimaryDiagnosisResults([]);
    } finally {
      setDiagnosisSearchLoading(false);
    }
  }, []);

  const fetchSecondaryDiagnosisResults = useCallback(async (query = "") => {
    setSecondaryDiagnosisSearchLoading(true);
    setSecondaryDiagnosisSearchError("");
    try {
      const response = await axios.get("https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search", {
        params: {
          sf: "code,name",
          df: "code,name",
          terms: query || "",
          maxList: 25,
        },
      });
      const data = response.data;
      if (Array.isArray(data) && Array.isArray(data[3])) {
        const formatted = data[3].map((item) => ({
          code: item[0],
          description: item[1],
        }));
        setSecondaryDiagnosisResults(formatted);
      } else {
        setSecondaryDiagnosisResults([]);
      }
    } catch (error) {
      setSecondaryDiagnosisSearchError("Unable to fetch diagnosis codes. Please try again.");
      setSecondaryDiagnosisResults([]);
    } finally {
      setSecondaryDiagnosisSearchLoading(false);
    }
  }, []);

  const fetchTertiaryDiagnosisResults = useCallback(async (query = "") => {
    setTertiaryDiagnosisSearchLoading(true);
    setTertiaryDiagnosisSearchError("");
    try {
      const response = await axios.get("https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search", {
        params: {
          sf: "code,name",
          df: "code,name",
          terms: query || "",
          maxList: 25,
        },
      });
      const data = response.data;
      if (Array.isArray(data) && Array.isArray(data[3])) {
        const formatted = data[3].map((item) => ({
          code: item[0],
          description: item[1],
        }));
        setTertiaryDiagnosisResults(formatted);
      } else {
        setTertiaryDiagnosisResults([]);
      }
    } catch (error) {
      setTertiaryDiagnosisSearchError("Unable to fetch diagnosis codes. Please try again.");
      setTertiaryDiagnosisResults([]);
    } finally {
      setTertiaryDiagnosisSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isPrimaryDiagnosisDropdownOpen) return;
    setPrimaryDiagnosisSearchTerm("");
    fetchPrimaryDiagnosisResults("");
  }, [isPrimaryDiagnosisDropdownOpen, fetchPrimaryDiagnosisResults]);

  useEffect(() => {
    if (!isPrimaryDiagnosisDropdownOpen) return;
    const trimmedTerm = primaryDiagnosisSearchTerm.trim();
    const handler = setTimeout(() => {
      fetchPrimaryDiagnosisResults(trimmedTerm);
    }, trimmedTerm ? 500 : 0);
    return () => clearTimeout(handler);
  }, [primaryDiagnosisSearchTerm, isPrimaryDiagnosisDropdownOpen, fetchPrimaryDiagnosisResults]);

  useEffect(() => {
    if (!isPrimaryDiagnosisDropdownOpen) return;
    const handleClickOutside = (event) => {
      if (diagnosisCodeRef.current && !diagnosisCodeRef.current.contains(event.target)) {
        setIsPrimaryDiagnosisDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isPrimaryDiagnosisDropdownOpen]);

  useEffect(() => {
    if (!isSecondaryDiagnosisDropdownOpen) return;
    setSecondaryDiagnosisSearchTerm("");
    fetchSecondaryDiagnosisResults("");
  }, [isSecondaryDiagnosisDropdownOpen, fetchSecondaryDiagnosisResults]);

  useEffect(() => {
    if (!isSecondaryDiagnosisDropdownOpen) return;
    const trimmedTerm = secondaryDiagnosisSearchTerm.trim();
    const handler = setTimeout(() => {
      fetchSecondaryDiagnosisResults(trimmedTerm);
    }, trimmedTerm ? 500 : 0);
    return () => clearTimeout(handler);
  }, [secondaryDiagnosisSearchTerm, isSecondaryDiagnosisDropdownOpen, fetchSecondaryDiagnosisResults]);

  useEffect(() => {
    if (!isSecondaryDiagnosisDropdownOpen) return;
    const handleClickOutside = (event) => {
      if (secondaryDiagnosisCodeRef.current && !secondaryDiagnosisCodeRef.current.contains(event.target)) {
        setIsSecondaryDiagnosisDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSecondaryDiagnosisDropdownOpen]);

  useEffect(() => {
    if (!isTertiaryDiagnosisDropdownOpen) return;
    setTertiaryDiagnosisSearchTerm("");
    fetchTertiaryDiagnosisResults("");
  }, [isTertiaryDiagnosisDropdownOpen, fetchTertiaryDiagnosisResults]);

  useEffect(() => {
    if (!isTertiaryDiagnosisDropdownOpen) return;
    const trimmedTerm = tertiaryDiagnosisSearchTerm.trim();
    const handler = setTimeout(() => {
      fetchTertiaryDiagnosisResults(trimmedTerm);
    }, trimmedTerm ? 500 : 0);
    return () => clearTimeout(handler);
  }, [tertiaryDiagnosisSearchTerm, isTertiaryDiagnosisDropdownOpen, fetchTertiaryDiagnosisResults]);

  useEffect(() => {
    if (!isTertiaryDiagnosisDropdownOpen) return;
    const handleClickOutside = (event) => {
      if (tertiaryDiagnosisCodeRef.current && !tertiaryDiagnosisCodeRef.current.contains(event.target)) {
        setIsTertiaryDiagnosisDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isTertiaryDiagnosisDropdownOpen]);

  // Handle overflow for substance textareas when they appear
  useEffect(() => {
    const checkOverflow = (textareaId, value) => {
      const textarea = document.getElementById(textareaId);
      if (textarea) {
        setTimeout(() => {
          if (!value || value.trim() === "") {
            textarea.style.overflowY = "hidden";
          } else {
            const hasOverflow = textarea.scrollHeight > textarea.clientHeight;
            textarea.style.overflowY = hasOverflow ? "auto" : "hidden";
          }
        }, 0);
      }
    };

    if (sessionFormData.alcohol && sessionFormData.alcohol !== "-" && sessionFormData.alcohol !== "None" && sessionFormData.alcohol !== "Unknown / Not Assessed") {
      checkOverflow("alcohol-details-textarea", sessionFormData.alcoholDetails);
    }
    if (sessionFormData.cannabis && sessionFormData.cannabis !== "-" && sessionFormData.cannabis !== "None" && sessionFormData.cannabis !== "Unknown / Not Assessed") {
      checkOverflow("cannabis-details-textarea", sessionFormData.cannabisDetails);
    }
    if (sessionFormData.tobacco && sessionFormData.tobacco !== "-" && sessionFormData.tobacco !== "None" && sessionFormData.tobacco !== "Unknown / Not Assessed") {
      checkOverflow("tobacco-details-textarea", sessionFormData.tobaccoDetails);
    }
    if (sessionFormData.illicitDrugs && sessionFormData.illicitDrugs !== "-" && sessionFormData.illicitDrugs !== "None" && sessionFormData.illicitDrugs !== "Unknown / Not Assessed") {
      checkOverflow("illicit-drugs-details-textarea", sessionFormData.illicitDrugsDetails);
    }
  }, [sessionFormData.alcohol, sessionFormData.cannabis, sessionFormData.tobacco, sessionFormData.illicitDrugs, sessionFormData.alcoholDetails, sessionFormData.cannabisDetails, sessionFormData.tobaccoDetails, sessionFormData.illicitDrugsDetails]);


  useEffect(() => {
    if (addAddendum && sessionFormData.addendumText) {
      autoResizeTextarea(document.getElementById("addendum-textarea"));
    }
  }, [addAddendum, sessionFormData.addendumText]);

  // Initialize addendumText based on addendumData when addAddendum becomes true
  useEffect(() => {
    // Reset initialization flag when addAddendum becomes false
    if (!addAddendum) {
      addendumInitializedRef.current = false;
      return;
    }

    // Only initialize once when addAddendum first becomes true and data is loaded
    if (addAddendum && dataLoaded && !addendumInitializedRef.current) {
      // Agar addendumData empty hai ya null hai to textarea empty rakh do
      if (!addendumData || addendumData.length === 0) {
        setSessionFormData(prev => ({ ...prev, addendumText: "" }));
        setCurrentAddendumIndex(null);
        addendumInitializedRef.current = true;
        return;
      }

      // Latest/last object ko find karo
      const lastAddendum = addendumData[addendumData.length - 1];
      const lastIndex = addendumData.length - 1;

      // Agar last object ka addendumSigned true hai to textarea empty rakh do (new entry create hoga)
      if (lastAddendum && lastAddendum.addendumSigned === true) {
        setSessionFormData(prev => ({ ...prev, addendumText: "" }));
        setCurrentAddendumIndex(null);
      }
      // Agar last object ka addendumSigned false hai to uska addendumText show karo
      else if (lastAddendum && lastAddendum.addendumSigned === false) {
        setSessionFormData(prev => ({ ...prev, addendumText: lastAddendum.addendumText || "" }));
        setCurrentAddendumIndex(lastIndex);
      } else {
        setSessionFormData(prev => ({ ...prev, addendumText: "" }));
        setCurrentAddendumIndex(null);
      }
      addendumInitializedRef.current = true;
    }
  }, [addAddendum, addendumData, dataLoaded]);


  useEffect(() => {
    const interval = setInterval(() => {

    }, 30000);

    return () => clearInterval(interval);
  }, [sessionFormData.safetyPlanNotes]);

  useEffect(() => {
    const interval = setInterval(() => {
    }, 30000);

    return () => clearInterval(interval);
  }, [sessionFormData.clientStrengthsDescription]);

  useEffect(() => {
    const interval = setInterval(() => {
    }, 30000);

    return () => clearInterval(interval);
  }, [sessionFormData.clientGoal]);

  useEffect(() => {
    if (!sessionFormData.reviewDate || sessionFormData.reviewDate === "") {
      const today = new Date();
      const reviewDate = new Date(today);
      reviewDate.setDate(today.getDate() + 180);
      const formattedDate = reviewDate.toISOString().split('T')[0];
      updateFormData("reviewDate", formattedDate);
    }
  }, []);

  const fetchSessionHistoryDetail = async () => {
    if (!session?._id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/get-by-session/${session._id}`,
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success && response.data.data) {
        const existingData = response.data.data;
        setSessionFormData(prev => ({
          reasonForSeekingServices: existingData.reasonForSeekingServices || "",
          selectedConcerns: existingData.selectedConcerns || [],
          otherConcernText: existingData.otherConcernText || "",
          duration: existingData.duration || "",
          frequency: existingData.frequency || "",
          severity: existingData.severity || "",
          freeTextHistory: existingData.freeTextHistory || "",
          selectedMedicalConditions: (existingData.selectedMedicalConditions && Array.isArray(existingData.selectedMedicalConditions) && existingData.selectedMedicalConditions.length > 0) ? existingData.selectedMedicalConditions : [],
          otherMedicalConditionText: existingData.otherMedicalConditionText || "",
          selectedPastPsychiatric: (existingData.selectedPastPsychiatric && Array.isArray(existingData.selectedPastPsychiatric) && existingData.selectedPastPsychiatric.length > 0) ? existingData.selectedPastPsychiatric : [],
          otherPastPsychiatricText: existingData.otherPastPsychiatricText || "",
          currentMedications: existingData.currentMedications || "",
          selectedFamilyPsychiatric: (existingData.selectedFamilyPsychiatric && Array.isArray(existingData.selectedFamilyPsychiatric) && existingData.selectedFamilyPsychiatric.length > 0) ? existingData.selectedFamilyPsychiatric : [],
          otherFamilyPsychiatricText: existingData.otherFamilyPsychiatricText || "",
          alcohol: existingData.alcohol || "",
          alcoholDetails: existingData.alcoholDetails || "",
          cannabis: existingData.cannabis || "",
          cannabisDetails: existingData.cannabisDetails || "",
          tobacco: existingData.tobacco || "",
          tobaccoDetails: existingData.tobaccoDetails || "",
          illicitDrugs: existingData.illicitDrugs || "",
          illicitDrugsDetails: existingData.illicitDrugsDetails || "",
          substanceUseDetails: existingData.substanceUseDetails || "",
          livingSituation: existingData.livingSituation || "",
          selectedSupportSystem: existingData.selectedSupportSystem || [],
          otherSupportSystemText: existingData.otherSupportSystemText || "",
          familySocialNotes: existingData.familySocialNotes || "",
          highestEducationLevel: existingData.highestEducationLevel || "",
          otherEducationLevelText: existingData.otherEducationLevelText || "",
          employmentStatus: existingData.employmentStatus || "",
          otherEmploymentStatusText: existingData.otherEmploymentStatusText || "",
          currentJobRole: existingData.currentJobRole || "",
          appearance: existingData.appearance || "",
          behavior: existingData.behavior || "",
          speech: existingData.speech || "",
          mood: existingData.mood || "",
          affect: existingData.affect || "",
          thoughtProcess: existingData.thoughtProcess || "",
          orientation: existingData.orientation || "",
          insightJudgment: existingData.insightJudgment || "",
          suicideRisk: existingData.suicideRisk || "",
          homicideRisk: existingData.homicideRisk || "",
          abuseNeglect: existingData.abuseNeglect || "",
          safetyPlanNotes: existingData.safetyPlanNotes || "",
          selectedStrengths: existingData.selectedStrengths || [],
          otherStrengthsText: existingData.otherStrengthsText || "",
          clientStrengthsDescription: existingData.clientStrengthsDescription || "",
          primaryDiagnosis: existingData.primaryDiagnosis || "",
          primaryDiagnosisDescription: existingData.primaryDiagnosisDescription || "",
          secondaryDiagnosis: existingData.secondaryDiagnosis || "",
          secondaryDiagnosisDescription: existingData.secondaryDiagnosisDescription || "",
          tertiaryDiagnosis: existingData.tertiaryDiagnosis || "",
          tertiaryDiagnosisDescription: existingData.tertiaryDiagnosisDescription || "",
          clientGoal: existingData.clientGoal || "",
          objectives: existingData.objectives || "",
          interventions: existingData.interventions || "",
          sessionFrequency: existingData.sessionFrequency || "",
          otherSessionFrequencyText: existingData.otherSessionFrequencyText || "",
          reviewDate: existingData.reviewDate || "",
          timeIn: existingData.timeIn || "",
          timeOut: existingData.timeOut || "",
          wasClientAtHome: existingData.wasClientAtHome || "",
          signedAt: existingData.signedAt || null,
          isSigned: prev.isSigned || existingData.isSigned || false
        }));


        // Set submission state from database
        if (existingData.isSubmit !== undefined) {
          setIsSubmit(existingData.isSubmit);
        }

        // Preserve isSigned state - only update if database has true, never reset to false
        const dbIsSigned = existingData.isSigned === true;
        setIsSigned(prev => prev || dbIsSigned);
        setIsSignedFromDB(dbIsSigned); // Store the database value

        // Set last saved timestamp if data exists
        if (existingData.updatedAt) {
          setLastSavedTimestamp(new Date(existingData.updatedAt));
        }

        // Fetch and set addendumData
        if (existingData.addendumData && Array.isArray(existingData.addendumData)) {
          const processedAddendumData = processAddendumData(existingData.addendumData);
          setAddendumData(processedAddendumData);

          // Update addendumText from latest addendum if it can be edited
          if (processedAddendumData && processedAddendumData.length > 0) {
            const latestIndex = processedAddendumData.length - 1;
            const latestAddendum = processedAddendumData[latestIndex];

            // If latest addendum can be edited (addendumSigned: false, addendumSubmit: true)
            if (latestAddendum &&
              latestAddendum.addendumSigned === false &&
              latestAddendum.addendumSubmit === true) {
              setSessionFormData(prev => ({
                ...prev,
                addendumText: latestAddendum.addendumText || ""
              }));
              setCurrentAddendumIndex(latestIndex);
            }
          }
        } else {
          setAddendumData([]);
        }

        setHasUnsavedChanges(false);
        setDataLoaded(true); // Mark data as loaded - data exists, no defaults needed
        clearAllErrors(); // Clear all errors when data is loaded
      } else {
        // No existing data - set defaults after marking as loaded
        setIsSubmit(false); // New records should have isSubmit as false
        setAddendumData([]); // Initialize addendumData as empty array
        setDataLoaded(true);
        setHasUnsavedChanges(false);
        // Set defaults for new form
        setSessionFormData(prev => ({
          ...prev,
        }));
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error("Error fetching session history detail:", error);
        // Don't change isSubmit - preserve existing state
        setAddendumData([]); // Initialize addendumData as empty array on error
        setDataLoaded(true); // Mark as loaded even on error
        setHasUnsavedChanges(false);
      } else {
        // 404 means no data exists - set defaults
        setIsSubmit(false); // New records should have isSubmit as false
        setAddendumData([]); // Initialize addendumData as empty array
        setDataLoaded(true);
        setHasUnsavedChanges(false);
        // Set defaults for new form
        setSessionFormData(prev => ({
          ...prev,
        }));
      }
    } finally {
      // Ensure loading is set to false only after all state updates are complete
      setLoading(false);
    }
  };

  // Handle openAddendum from location state - open addendum section when data is loaded
  useEffect(() => {
    if (openAddendum && dataLoaded && session?._id) {
      // Ensure isSubmit is true to show addendum view
      setIsSubmit(true);
      setAddAddendum(true);
      setActiveSection("add-addendum");

      // Scroll to addendum section after a short delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.getElementById("add-addendum");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 300);
    }
  }, [openAddendum, dataLoaded, session?._id]);

  // Error state setters mapping for helper functions
  const errorSettersMap = {
    checkboxError: setCheckboxError,
    durationError: setDurationError,
    frequencyError: setFrequencyError,
    severityError: setSeverityError,
    alcoholError: setAlcoholError,
    cannabisError: setCannabisError,
    tobaccoError: setTobaccoError,
    illicitDrugsError: setIllicitDrugsError,
    livingSituationError: setLivingSituationError,
    supportSystemError: setSupportSystemError,
    medicalConditionsError: setMedicalConditionsError,
    educationLevelError: setEducationLevelError,
    employmentStatusError: setEmploymentStatusError,
    appearanceError: setAppearanceError,
    behaviorError: setBehaviorError,
    speechError: setSpeechError,
    moodError: setMoodError,
    affectError: setAffectError,
    thoughtProcessError: setThoughtProcessError,
    orientationError: setOrientationError,
    insightJudgmentError: setInsightJudgmentError,
    suicideRiskError: setSuicideRiskError,
    homicideRiskError: setHomicideRiskError,
    abuseNeglectError: setAbuseNeglectError,
    safetyPlanNotesError: setSafetyPlanNotesError,
    strengthsError: setStrengthsError,
    clientGoalError: setClientGoalError,
    objectivesError: setObjectivesError,
    interventionsError: setInterventionsError,
    sessionFrequencyError: setSessionFrequencyError,
    reviewDateError: setReviewDateError,
    primaryDiagnosisError: setPrimaryDiagnosisError,
    addendumTextError: setAddendumTextError,
  };

  // Helper function to set error states from errorSetters object
  const setErrorStates = (errorSetters) => {
    Object.keys(errorSetters).forEach((errorKey) => {
      if (errorSetters[errorKey] && errorSettersMap[errorKey]) {
        errorSettersMap[errorKey](errorSetters[errorKey]);
      }
    });
  };

  const clearAllErrors = () => {
    // Use mapping to clear all errors efficiently
    Object.values(errorSettersMap).forEach((setter) => {
      setter("");
    });
  };

  // Build save payload
  const buildSavePayload = useCallback((isAutoSave = false) => {
    let totalDuration = null;

    const startMinutes = getMinutesFromTimeString(sessionFormData.timeIn);
    const endMinutes = getMinutesFromTimeString(sessionFormData.timeOut);

    if (startMinutes !== null && endMinutes !== null) {
      let diff = endMinutes - startMinutes;
      if (diff < 0) {
        diff += 24 * 60;
      }
      if (diff > 0) {
        totalDuration = diff;
      }
    }

    return {
      sessionId: session._id,
      isAutoSave: isAutoSave,
      // Presenting Concerns
      reasonForSeekingServices: sessionFormData.reasonForSeekingServices || "",
      selectedConcerns: sessionFormData.selectedConcerns || [],
      otherConcernText: sessionFormData.otherConcernText || "",
      // History of Presenting Concerns
      duration: sessionFormData.duration || "",
      totalDuration: totalDuration,
      frequency: sessionFormData.frequency || "",
      severity: sessionFormData.severity || "",
      freeTextHistory: sessionFormData.freeTextHistory || "",
      // Medical & Psychiatric History
      selectedMedicalConditions: sessionFormData.selectedMedicalConditions || [],
      otherMedicalConditionText: sessionFormData.otherMedicalConditionText || "",
      selectedPastPsychiatric: sessionFormData.selectedPastPsychiatric || [],
      otherPastPsychiatricText: sessionFormData.otherPastPsychiatricText || "",
      currentMedications: sessionFormData.currentMedications || "",
      selectedFamilyPsychiatric: sessionFormData.selectedFamilyPsychiatric || [],
      otherFamilyPsychiatricText: sessionFormData.otherFamilyPsychiatricText || "",
      // Substance Use
      alcohol: sessionFormData.alcohol || "",
      alcoholDetails: sessionFormData.alcoholDetails || "",
      cannabis: sessionFormData.cannabis || "",
      cannabisDetails: sessionFormData.cannabisDetails || "",
      tobacco: sessionFormData.tobacco || "",
      tobaccoDetails: sessionFormData.tobaccoDetails || "",
      illicitDrugs: sessionFormData.illicitDrugs || "",
      illicitDrugsDetails: sessionFormData.illicitDrugsDetails || "",
      substanceUseDetails: sessionFormData.substanceUseDetails || "",
      // Family & Social History
      livingSituation: sessionFormData.livingSituation || "",
      selectedSupportSystem: sessionFormData.selectedSupportSystem || [],
      otherSupportSystemText: sessionFormData.otherSupportSystemText || "",
      familySocialNotes: sessionFormData.familySocialNotes || "",
      // Education & Occupation
      highestEducationLevel: sessionFormData.highestEducationLevel || "",
      otherEducationLevelText: sessionFormData.otherEducationLevelText || "",
      employmentStatus: sessionFormData.employmentStatus || "",
      otherEmploymentStatusText: sessionFormData.otherEmploymentStatusText || "",
      currentJobRole: sessionFormData.currentJobRole || "",
      // Mental Status Exam
      appearance: sessionFormData.appearance || "",
      behavior: sessionFormData.behavior || "",
      speech: sessionFormData.speech || "",
      mood: sessionFormData.mood || "",
      affect: sessionFormData.affect || "",
      thoughtProcess: sessionFormData.thoughtProcess || "",
      orientation: sessionFormData.orientation || "",
      insightJudgment: sessionFormData.insightJudgment || "",
      // Risk & Safety
      suicideRisk: sessionFormData.suicideRisk || "",
      homicideRisk: sessionFormData.homicideRisk || "",
      abuseNeglect: sessionFormData.abuseNeglect || "",
      safetyPlanNotes: sessionFormData.safetyPlanNotes || "",
      // Strengths & Resources
      selectedStrengths: sessionFormData.selectedStrengths || [],
      otherStrengthsText: sessionFormData.otherStrengthsText || "",
      clientStrengthsDescription: sessionFormData.clientStrengthsDescription || "",
      // Diagnosis
      primaryDiagnosis: sessionFormData.primaryDiagnosis || "",
      primaryDiagnosisDescription: sessionFormData.primaryDiagnosisDescription || "",
      secondaryDiagnosis: sessionFormData.secondaryDiagnosis || "",
      secondaryDiagnosisDescription: sessionFormData.secondaryDiagnosisDescription || "",
      tertiaryDiagnosis: sessionFormData.tertiaryDiagnosis || "",
      tertiaryDiagnosisDescription: sessionFormData.tertiaryDiagnosisDescription || "",
      // Care Plan
      clientGoal: sessionFormData.clientGoal || "",
      objectives: sessionFormData.objectives || "",
      interventions: sessionFormData.interventions || "",
      sessionFrequency: sessionFormData.sessionFrequency || "",
      otherSessionFrequencyText: sessionFormData.otherSessionFrequencyText || "",
      reviewDate: sessionFormData.reviewDate || "",
      // Time fields
      timeIn: sessionFormData.timeIn || "",
      timeOut: sessionFormData.timeOut || "",
      wasClientAtHome: sessionFormData.wasClientAtHome || "",
      // Preserve isSigned from database - never send false if it was true in DB
      // Only allow setting to true explicitly, never reset to false
      isSigned: isSignedFromDB || sessionFormData.isSigned || false,
      addendumData: (() => {
        // Agar addAddendum true hai to addendumData array ko handle karo
        if (addAddendum && sessionFormData.addendumText) {
          const updatedAddendumData = [...addendumData];

          // Agar addendumData empty hai to new entry create karo
          if (!updatedAddendumData || updatedAddendumData.length === 0) {
            updatedAddendumData.push({
              addendumText: sessionFormData.addendumText,
              addendumSigned: false,
              addendumSubmit: false,
              addendumSignedAt: null,
              createdAt: new Date().toISOString()
            });
          } else {
            // Latest entry ko get karo
            const lastIndex = updatedAddendumData.length - 1;
            const lastAddendum = updatedAddendumData[lastIndex];

            // Agar latest entry ka addendumSigned true hai to new entry create karo
            if (lastAddendum && lastAddendum.addendumSigned === true) {
              updatedAddendumData.push({
                addendumText: sessionFormData.addendumText,
                addendumSigned: false,
                addendumSubmit: false,
                addendumSignedAt: null,
                createdAt: new Date().toISOString()
              });
            }
            // Agar latest entry ka addendumSigned false hai to us entry ko update karo
            else if (lastAddendum && lastAddendum.addendumSigned === false) {
              updatedAddendumData[lastIndex] = {
                ...updatedAddendumData[lastIndex],
                addendumText: sessionFormData.addendumText,
                addendumSigned: false,
                // addendumSubmit ki existing value ko preserve karo
                addendumSubmit: updatedAddendumData[lastIndex].addendumSubmit ?? false,
                addendumSignedAt: null,
                updatedAt: new Date().toISOString()
              };
            }
            // Fallback: agar koi condition match nahi karti to new entry create karo
            else {
              updatedAddendumData.push({
                addendumText: sessionFormData.addendumText,
                addendumSigned: false,
                addendumSubmit: false,
                addendumSignedAt: null,
                createdAt: new Date().toISOString()
              });
            }
          }
          // Process the array to ensure all entries have addendumSignedAt field properly set
          return processAddendumData(updatedAddendumData);
        }
        // Agar addAddendum false hai to existing addendumData ko preserve karo
        // Signed entries (addendumSigned === true) are locked and will be preserved as-is
        return processAddendumData(addendumData);
      })(),
    };
  }, [session?._id, sessionFormData, getMinutesFromTimeString, isSignedFromDB, addAddendum, addendumData, currentAddendumIndex, processAddendumData]);

  // Auto-save function
  const handleAutoSave = useCallback(async (showIndicator = false) => {
    if (!session?._id || !hasUnsavedChanges) {
      return; // No unsaved changes, skip auto-save
    }

    if (autoSaving || saving) {
      return; // Already saving, skip
    }

    if (!dataLoaded || (isSubmit && !addAddendum)) {
      return;
    }

    try {
      if (showIndicator) {
        setAutoSaving(true);
      }

      const payload = buildSavePayload(true); // Pass isAutoSave: true
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/createSessionHistory`,
        payload,
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success) {
        const currentData = JSON.stringify(payload);
        setLastSavedTimestamp(new Date());
        setHasUnsavedChanges(false);
        setLastSavedData(currentData);

        // Update addendumData state if addAddendum is true
        if (addAddendum && payload.addendumData) {
          setAddendumData(processAddendumData(payload.addendumData));
        }

        clearAllErrors(); // Clear all errors on successful auto-save
        errorToastShownRef.current = false; // Reset error toast state on successful save
        toast.dismiss("auto-save-error"); // Dismiss error toast if it exists

        // Clear retry timeout if exists
        if (retryTimeoutId) {
          clearTimeout(retryTimeoutId);
          setRetryTimeoutId(null);
        }

        if (showIndicator) {
          toast.success("💾 Progress saved.", {
            position: "bottom-right",
            autoClose: 2000,
            hideProgressBar: true,
          });
        }
      }
    } catch (error) {
      console.error("Auto-save failed:", error);

      // Show error banner only once
      if (!errorToastShownRef.current) {
        toast.error("⚠️ Unable to save changes — retrying automatically.", {
          position: "bottom-right",
          autoClose: false,
          hideProgressBar: false,
          toastId: "auto-save-error", // Use same toastId to prevent duplicates
        });
        errorToastShownRef.current = true;
      }

      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }

      // Retry after 15 seconds - but only if isSubmit is false OR addAddendum is true
      // Skip retry if isSubmit is true and addAddendum is false
      if (!(isSubmit && !addAddendum)) {
        const timeoutId = setTimeout(() => {
          handleAutoSave(false);
        }, 15000);
        setRetryTimeoutId(timeoutId);
      }
    } finally {
      if (showIndicator) {
        setAutoSaving(false);
      }
    }
  }, [session?._id, hasUnsavedChanges, autoSaving, saving, buildSavePayload, user?.user?.token, retryTimeoutId, isSubmit, dataLoaded, addAddendum]);

  useEffect(() => {
    if (!session?._id || !hasUnsavedChanges || !dataLoaded || (isSubmit && !addAddendum)) {
      return;
    }

    const interval = setInterval(() => {
      handleAutoSave(false); // Auto-save without showing indicator
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [hasUnsavedChanges, session?._id, handleAutoSave, isSubmit, dataLoaded, addAddendum]);

  useEffect(() => {
    if (activeSection && hasUnsavedChanges && dataLoaded && !(isSubmit && !addAddendum)) {
      handleAutoSave(false);
    }
  }, [activeSection, hasUnsavedChanges, handleAutoSave, isSubmit, dataLoaded, addAddendum]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && dataLoaded && !(isSubmit && !addAddendum)) {
        handleAutoSave(true); // Show "Saving progress..." indicator
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Saving progress...";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges, handleAutoSave, isSubmit, dataLoaded, addAddendum]);

  const handleEdit = async (e) => {
    e.stopPropagation();

    if (!session?._id) {
      toast.error("Session ID is missing");
      return;
    }

    try {
      setSaving(true);
      const payload = buildSavePayload(true);
      payload.isSubmit = false; // Explicitly set isSubmit to false for Edit

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/createSessionHistory`,
        payload,
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success) {
        setIsSubmit(false);
        toast.info("You can now edit the session history detail");
        // Refetch data to ensure consistency
        // await fetchSessionHistoryDetail();
      } else {
        toast.error(response.data.message || "Failed to update session history detail");
      }
    } catch (error) {
      console.error("Error updating session history detail:", error);
      toast.error("Unable to update. Please check your connection.");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (e) => {
    e.stopPropagation();

    if (!session?._id) {
      toast.error("Session ID is missing");
      return;
    }

    try {
      setSaving(true);
      setSaveStatus("Saving...");
      const payload = buildSavePayload(true); // Manual save - no validation, save whatever data is present
      payload.isSaveDiagnosis = true;
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/createSessionHistory`,
        payload,
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success) {
        const currentData = JSON.stringify(payload);
        setLastSavedTimestamp(new Date());
        setHasUnsavedChanges(false);
        setLastSavedData(currentData);
        setSaveStatus("Saved");

        // Update addendumData state if addAddendum is true
        if (addAddendum && payload.addendumData) {
          setAddendumData(processAddendumData(payload.addendumData));
        }

        clearAllErrors(); // Clear all errors on successful save
        errorToastShownRef.current = false; // Reset error toast state on successful save
        toast.dismiss("auto-save-error"); // Dismiss error toast if it exists

        toast.success("💾 Assessment saved successfully.", {
          position: "bottom-right",
          autoClose: 3000,
        });

        // Revert button text after 2 seconds
        setTimeout(() => {
          setSaveStatus("Save");
        }, 2000);
      } else {
        // Show all validation errors from backend
        if (response.data.errors && Array.isArray(response.data.errors)) {
          response.data.errors.forEach((error, index) => {
            setTimeout(() => {
              toast.error(error);
            }, index * 300);
          });
        } else {
          toast.error(response.data.message || "Failed to save session history detail");
        }
        setSaveStatus("Save");
      }
    } catch (error) {
      console.error("Error saving session history detail:", error);
      setSaveStatus("Save");

      // Show error banner
      toast.error("Unable to save progress. Please check your connection.", {
        position: "bottom-right",
        autoClose: false,
      });

      // Show all validation errors from backend
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        error.response.data.errors.forEach((err, index) => {
          setTimeout(() => {
            toast.error(err);
          }, index * 300);
        });
      } else {
        toast.error(
          error.response?.data?.message ||
          "An error occurred while saving session history detail"
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSignAndSubmit = async (e) => {
    e?.stopPropagation?.();

    if (!session?._id) {
      toast.error("Session ID is missing");
      return;
    }

    // Collect all validation errors
    const errors = [];
    const errorSetters = {};

    // Presenting Concerns
    if (!sessionFormData.selectedConcerns || sessionFormData.selectedConcerns.length === 0) {
      errors.push("At least one presenting concern checkbox is required");
      errorSetters.checkboxError = "At least one checkbox required";
    }

    // History of Presenting Concerns
    if (!sessionFormData.duration || sessionFormData.duration === "") {
      errors.push("Duration must be selected");
      errorSetters.durationError = "Duration must be selected";
    }

    if (sessionFormData.duration && sessionFormData.duration !== "" && (!sessionFormData.frequency || sessionFormData.frequency === "")) {
      errors.push("Frequency must be selected when duration is filled");
      errorSetters.frequencyError = "Frequency must be selected when duration is filled";
    }

    if (!sessionFormData.severity || sessionFormData.severity === "") {
      errors.push("Severity must be selected");
      errorSetters.severityError = "Severity must be selected";
    }

    // Family & Social History
    if (!sessionFormData.livingSituation || sessionFormData.livingSituation === "") {
      errors.push("Living Situation must be selected");
      errorSetters.livingSituationError = "Living Situation must be selected";
    }

    if (!sessionFormData.selectedSupportSystem || sessionFormData.selectedSupportSystem.length === 0) {
      errors.push("At least one support system option must be selected");
      errorSetters.supportSystemError = "At least one support system option must be selected";
    }

    // Medical History - REQUIRED
    if (!sessionFormData.selectedMedicalConditions || sessionFormData.selectedMedicalConditions.length === 0) {
      errors.push("At least one medical condition checkbox is required");
      errorSetters.medicalConditionsError = "At least one medical condition checkbox is required";
    }

    // Education & Occupation
    if (!sessionFormData.highestEducationLevel || sessionFormData.highestEducationLevel === "") {
      errors.push("Highest Education Level must be selected");
      errorSetters.educationLevelError = "Highest Education Level must be selected";
    }

    if (!sessionFormData.employmentStatus || sessionFormData.employmentStatus === "") {
      errors.push("Employment Status must be selected");
      errorSetters.employmentStatusError = "Employment Status must be selected";
    }

    // Mental Status Exam
    if (!sessionFormData.appearance || sessionFormData.appearance === "") {
      errors.push("Appearance (Mental Status Exam) must be selected");
      errorSetters.appearanceError = "Appearance must be selected";
    }

    if (!sessionFormData.behavior || sessionFormData.behavior === "") {
      errors.push("Behavior (Mental Status Exam) must be selected");
      errorSetters.behaviorError = "Behavior must be selected";
    }

    if (!sessionFormData.speech || sessionFormData.speech === "") {
      errors.push("Speech (Mental Status Exam) must be selected");
      errorSetters.speechError = "Speech must be selected";
    }

    if (!sessionFormData.mood || sessionFormData.mood === "") {
      errors.push("Mood (Mental Status Exam) must be selected");
      errorSetters.moodError = "Mood must be selected";
    }

    if (!sessionFormData.affect || sessionFormData.affect === "") {
      errors.push("Affect (Mental Status Exam) must be selected");
      errorSetters.affectError = "Affect must be selected";
    }

    if (!sessionFormData.thoughtProcess || sessionFormData.thoughtProcess === "") {
      errors.push("Thought Process (Mental Status Exam) must be selected");
      errorSetters.thoughtProcessError = "Thought Process must be selected";
    }

    if (!sessionFormData.orientation || sessionFormData.orientation === "") {
      errors.push("Orientation (Mental Status Exam) must be selected");
      errorSetters.orientationError = "Orientation must be selected";
    }

    if (!sessionFormData.insightJudgment || sessionFormData.insightJudgment === "") {
      errors.push("Insight/Judgment (Mental Status Exam) must be selected");
      errorSetters.insightJudgmentError = "Insight/Judgment must be selected";
    }

    // Risk & Safety
    if (!sessionFormData.suicideRisk || sessionFormData.suicideRisk === "") {
      errors.push("Suicide Risk must be selected");
      errorSetters.suicideRiskError = "Suicide Risk must be selected";
    }

    if (!sessionFormData.homicideRisk || sessionFormData.homicideRisk === "") {
      errors.push("Homicide Risk must be selected");
      errorSetters.homicideRiskError = "Homicide Risk must be selected";
    }

    if (!sessionFormData.abuseNeglect || sessionFormData.abuseNeglect === "") {
      errors.push("Abuse/Neglect must be selected");
      errorSetters.abuseNeglectError = "Abuse/Neglect must be selected";
    }

    const hasNonNoneRisk =
      (sessionFormData.suicideRisk && sessionFormData.suicideRisk !== "" && sessionFormData.suicideRisk !== "None") ||
      (sessionFormData.homicideRisk && sessionFormData.homicideRisk !== "" && sessionFormData.homicideRisk !== "None") ||
      (sessionFormData.abuseNeglect && sessionFormData.abuseNeglect !== "" && sessionFormData.abuseNeglect !== "None");

    if (hasNonNoneRisk && (!sessionFormData.safetyPlanNotes || sessionFormData.safetyPlanNotes.trim() === "")) {
      errors.push("Safety Plan / Notes is required when any risk field is not 'None'");
      errorSetters.safetyPlanNotesError = "Safety Plan / Notes is required when any risk field is not 'None'.";
    }

    const hasSelectedStrengths = sessionFormData.selectedStrengths &&
      Array.isArray(sessionFormData.selectedStrengths) &&
      sessionFormData.selectedStrengths.length > 0;

    if (!hasSelectedStrengths) {
      errors.push("At least one strength option must be selected");
      errorSetters.strengthsError = "At least one strength option must be selected";
    }

    // Diagnosis - Primary Diagnosis is REQUIRED
    if (!sessionFormData.primaryDiagnosis || sessionFormData.primaryDiagnosis.trim() === "" || sessionFormData.primaryDiagnosis === "-") {
      errors.push("Primary Diagnosis is required");
      errorSetters.primaryDiagnosisError = "Primary Diagnosis is required. Must select one ICD-10 diagnosis.";
    }

    // Care Plan
    if (!sessionFormData.clientGoal || sessionFormData.clientGoal.trim() === "") {
      errors.push("Client Goal is required");
      errorSetters.clientGoalError = "Client Goal is required";
    }

    if (!sessionFormData.objectives || sessionFormData.objectives.trim() === "") {
      errors.push("Objectives is required");
      errorSetters.objectivesError = "Objectives is required";
    }

    if (!sessionFormData.interventions || sessionFormData.interventions.trim() === "") {
      errors.push("Interventions is required");
      errorSetters.interventionsError = "Interventions is required";
    }

    if (!sessionFormData.sessionFrequency || sessionFormData.sessionFrequency === "") {
      errors.push("Session Frequency is required");
      errorSetters.sessionFrequencyError = "Session Frequency is required";
    }

    if (sessionFormData.sessionFrequency === "Other" && (!sessionFormData.otherSessionFrequencyText || sessionFormData.otherSessionFrequencyText.trim() === "")) {
      errors.push("Please specify frequency");
      errorSetters.sessionFrequencyError = "Please specify frequency";
    }

    if (!sessionFormData.reviewDate || sessionFormData.reviewDate === "") {
      errors.push("Review Date is required");
      errorSetters.reviewDateError = "Review Date is required";
    }

    // Substance Use - All fields are required
    if (!sessionFormData.alcohol || sessionFormData.alcohol === "" || sessionFormData.alcohol === "-") {
      errors.push("Alcohol must be selected");
      errorSetters.alcoholError = "Alcohol must be selected";
    }

    if (!sessionFormData.cannabis || sessionFormData.cannabis === "" || sessionFormData.cannabis === "-") {
      errors.push("Cannabis must be selected");
      errorSetters.cannabisError = "Cannabis must be selected";
    }

    if (!sessionFormData.tobacco || sessionFormData.tobacco === "" || sessionFormData.tobacco === "-") {
      errors.push("Tobacco must be selected");
      errorSetters.tobaccoError = "Tobacco must be selected";
    }

    if (!sessionFormData.illicitDrugs || sessionFormData.illicitDrugs === "" || sessionFormData.illicitDrugs === "-") {
      errors.push("Illicit Drugs must be selected");
      errorSetters.illicitDrugsError = "Illicit Drugs must be selected";
    }

    // Set all error states using helper function
    setErrorStates(errorSetters);

    // Show all error toasts
    if (errors.length > 0) {
      errors.forEach((error, index) => {
        setTimeout(() => {
          toast.error(error);
        }, index * 300); // Stagger toasts by 300ms
      });
      return;
    }

    try {
      setSigningAndSubmitting(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/createSessionHistory`,
        {
          sessionId: session._id,
          isAutoSave: false,
          isSubmit: true,
          // Presenting Concerns
          reasonForSeekingServices: sessionFormData.reasonForSeekingServices || "",
          selectedConcerns: sessionFormData.selectedConcerns || [],
          otherConcernText: sessionFormData.otherConcernText || "",
          // History of Presenting Concerns
          duration: sessionFormData.duration || "",
          frequency: sessionFormData.frequency || "",
          severity: sessionFormData.severity || "",
          freeTextHistory: sessionFormData.freeTextHistory || "",
          // Medical & Psychiatric History
          selectedMedicalConditions: sessionFormData.selectedMedicalConditions || [],
          otherMedicalConditionText: sessionFormData.otherMedicalConditionText || "",
          selectedPastPsychiatric: sessionFormData.selectedPastPsychiatric || [],
          otherPastPsychiatricText: sessionFormData.otherPastPsychiatricText || "",
          currentMedications: sessionFormData.currentMedications || "",
          selectedFamilyPsychiatric: sessionFormData.selectedFamilyPsychiatric || [],
          otherFamilyPsychiatricText: sessionFormData.otherFamilyPsychiatricText || "",
          // Substance Use
          alcohol: sessionFormData.alcohol || "",
          cannabis: sessionFormData.cannabis || "",
          tobacco: sessionFormData.tobacco || "",
          illicitDrugs: sessionFormData.illicitDrugs || "",
          substanceUseDetails: sessionFormData.substanceUseDetails || "",
          // Family & Social History
          livingSituation: sessionFormData.livingSituation || "",
          selectedSupportSystem: sessionFormData.selectedSupportSystem || [],
          otherSupportSystemText: sessionFormData.otherSupportSystemText || "",
          familySocialNotes: sessionFormData.familySocialNotes || "",
          // Education & Occupation
          highestEducationLevel: sessionFormData.highestEducationLevel || "",
          otherEducationLevelText: sessionFormData.otherEducationLevelText || "",
          employmentStatus: sessionFormData.employmentStatus || "",
          otherEmploymentStatusText: sessionFormData.otherEmploymentStatusText || "",
          currentJobRole: sessionFormData.currentJobRole || "",
          // Mental Status Exam
          appearance: sessionFormData.appearance || "",
          behavior: sessionFormData.behavior || "",
          speech: sessionFormData.speech || "",
          mood: sessionFormData.mood || "",
          affect: sessionFormData.affect || "",
          thoughtProcess: sessionFormData.thoughtProcess || "",
          orientation: sessionFormData.orientation || "",
          insightJudgment: sessionFormData.insightJudgment || "",
          // Risk & Safety
          suicideRisk: sessionFormData.suicideRisk || "",
          homicideRisk: sessionFormData.homicideRisk || "",
          abuseNeglect: sessionFormData.abuseNeglect || "",
          safetyPlanNotes: sessionFormData.safetyPlanNotes || "",
          // Strengths & Resources
          selectedStrengths: sessionFormData.selectedStrengths || [],
          otherStrengthsText: sessionFormData.otherStrengthsText || "",
          clientStrengthsDescription: sessionFormData.clientStrengthsDescription || "",
          // Diagnosis
          primaryDiagnosis: sessionFormData.primaryDiagnosis || "",
          primaryDiagnosisDescription: sessionFormData.primaryDiagnosisDescription || "",
          secondaryDiagnosis: sessionFormData.secondaryDiagnosis || "",
          secondaryDiagnosisDescription: sessionFormData.secondaryDiagnosisDescription || "",
          tertiaryDiagnosis: sessionFormData.tertiaryDiagnosis || "",
          tertiaryDiagnosisDescription: sessionFormData.tertiaryDiagnosisDescription || "",
          // Care Plan
          clientGoal: sessionFormData.clientGoal || "",
          objectives: sessionFormData.objectives || "",
          interventions: sessionFormData.interventions || "",
          sessionFrequency: sessionFormData.sessionFrequency || "",
          otherSessionFrequencyText: sessionFormData.otherSessionFrequencyText || "",
          reviewDate: sessionFormData.reviewDate || "",
          // Time fields
          timeIn: sessionFormData.timeIn || "",
          timeOut: sessionFormData.timeOut || "",
          wasClientAtHome: sessionFormData.wasClientAtHome || "",
          // Preserve isSigned from database
          isSigned: isSignedFromDB || sessionFormData.isSigned || false
        },
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success) {
        setIsSubmit(true);
        clearAllErrors(); // Clear all errors on successful sign and submit
        toast.success("Session history detail signed and submitted successfully!", {
          position: "bottom-right",
          autoClose: 3000,
        });
      } else {
        // Show all validation errors from backend
        if (response.data.errors && Array.isArray(response.data.errors)) {
          response.data.errors.forEach((error, index) => {
            setTimeout(() => {
              toast.error(error);
            }, index * 300);
          });
        } else {
          toast.error(response.data.message || "Failed to sign and submit session history detail");
        }
      }
    } catch (error) {
      console.error("Error signing and submitting session history detail:", error);
      // Show all validation errors from backend
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        error.response.data.errors.forEach((err, index) => {
          setTimeout(() => {
            toast.error(err);
          }, index * 300);
        });
      } else {
        toast.error(
          error.response?.data?.message ||
          "An error occurred while signing and submitting session history detail"
        );
      }
    } finally {
      setSigningAndSubmitting(false);
    }
  };

  // New function for addendum submit - checks if latest addendumText is not empty
  const handleAddendumSubmit = async (e) => {
    e?.stopPropagation?.();

    if (!session?._id) {
      toast.error("Session ID is missing");
      return;
    }

    // Check: Agar latest addendumData entry ka addendumText empty hai to return karo
    if (!addendumData || addendumData.length === 0) {
      setAddendumTextError("Please add addendum text before submitting");
      toast.error("Please add addendum text before submitting");
      return;
    }

    const latestIndex = addendumData.length - 1;
    const latestAddendum = addendumData[latestIndex];

    // Agar latest entry ka addendumSigned true hai to error throw karo
    if (latestAddendum && latestAddendum.addendumSigned === true) {
      setAddendumTextError("Please add addendum text before submitting");
      toast.error("Please add addendum text before submitting");
      return;
    }

    if (!latestAddendum || !latestAddendum.addendumText || latestAddendum.addendumText.trim() === "") {
      setAddendumTextError("Please add addendum text before submitting");
      toast.error("Please add addendum text before submitting");
      return;
    }

    setAddendumTextError("");

    try {
      setSigningAndSubmitting(true);

      const payload = buildSavePayload(false);

      // Latest addendumData entry ka addendumSubmit true karo
      if (payload.addendumData && Array.isArray(payload.addendumData) && payload.addendumData.length > 0) {
        const latestPayloadIndex = payload.addendumData.length - 1;
        const latestPayloadAddendum = payload.addendumData[latestPayloadIndex];

        // Latest entry ka addendumSubmit true karo
        if (latestPayloadAddendum) {
          payload.addendumData[latestPayloadIndex] = {
            ...latestPayloadAddendum,
            addendumSubmit: true
          };
        }
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/createSessionHistory`,
        payload,
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success) {
        // Update addendumData state immediately with the submitted data
        if (payload.addendumData && Array.isArray(payload.addendumData) && payload.addendumData.length > 0) {
          const updatedAddendumData = [...payload.addendumData];
          setAddendumData(updatedAddendumData);

          // Update addendumText from latest addendum
          const latestIndex = updatedAddendumData.length - 1;
          const latestAddendum = updatedAddendumData[latestIndex];
          if (latestAddendum && latestAddendum.addendumText) {
            setSessionFormData(prev => ({
              ...prev,
              addendumText: latestAddendum.addendumText || ""
            }));
            setCurrentAddendumIndex(latestIndex);
          }
        }

        setIsSubmit(true);
        setAddAddendum(false); // Reset addAddendum after successful submit

        clearAllErrors(); // Clear all errors on successful submit
        toast.success("Addendum submitted successfully!", {
          position: "bottom-right",
          autoClose: 3000,
        });
      } else {
        // Show all validation errors from backend
        if (response.data.errors && Array.isArray(response.data.errors)) {
          response.data.errors.forEach((error, index) => {
            setTimeout(() => {
              toast.error(error);
            }, index * 300);
          });
        } else {
          toast.error(response.data.message || "Failed to submit addendum");
        }
      }
    } catch (error) {
      console.error("Error submitting addendum:", error);
      // Show all validation errors from backend
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        error.response.data.errors.forEach((err, index) => {
          setTimeout(() => {
            toast.error(err);
          }, index * 300);
        });
      } else {
        toast.error(
          error.response?.data?.message ||
          "An error occurred while submitting addendum"
        );
      }
    } finally {
      setSigningAndSubmitting(false);
    }
  };

  const handleSignAndLock = async () => {
    if (!session?._id) {
      toast.error("Session ID is missing");
      return;
    }

    try {
      setSigningAndSubmitting(true);

      // Optimized: If signing addendum, only send minimal data
      if (canEditAddendum() && addendumData && Array.isArray(addendumData) && addendumData.length > 0) {
        const latestIndex = addendumData.length - 1;
        const latestAddendum = addendumData[latestIndex];

        if (latestAddendum && latestAddendum.addendumSigned === false) {
          try {
            // Create optimized payload with only sessionId and updated addendumData
            const updatedAddendumData = [...addendumData];
            updatedAddendumData[latestIndex] = {
              ...latestAddendum,
              addendumSigned: true,
              // Backend will set addendumSignedAt automatically, but we can set it here for immediate UI update
              addendumSignedAt: new Date().toISOString()
            };

            // Send minimal payload - only sessionId and addendumData
            const response = await axios.post(
              `${process.env.REACT_APP_BASE_URL}/sessionHistory/createSessionHistory`,
              {
                sessionId: session._id,
                addendumData: updatedAddendumData,
                isSigned: true,
                isAutoSave: true,
              },
              getConfigWithToken(user.user?.token)
            );

            if (response.data.success) {
              toast.success("Note signed and locked successfully.", {
                position: "bottom-right",
                autoClose: 3000,
              });

              // Get signedAt timestamp from response
              const signedAtTimestamp = response.data.data?.signedAt || new Date().toISOString();

              setSessionFormData(prev => ({
                ...prev,
                isSigned: true,
                signedAt: signedAtTimestamp
              }));
              setIsSigned(true);
              setIsSignedFromDB(true);
              setIsSubmit(true);

              // Update addendumData state with signed entry
              setAddendumData(updatedAddendumData);
              return; // Early return - don't execute the full payload code below
            } else {
              toast.error(response.data.message || "Failed to sign and lock note.");
              return;
            }
          } catch (error) {
            console.error("Error signing addendum:", error);
            toast.error(
              error.response?.data?.message ||
              "An error occurred while signing addendum."
            );
            return;
          }
        }
      }

      // Optimized: For normal sign & lock (not addendum), only send sessionId and isSigned
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/createSessionHistory`,
        {
          sessionId: session._id,
          isSigned: true,
          isAutoSave: true,
          carePlanAction: "create"
        },
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success) {
        toast.success("Note signed and locked successfully.", {
          position: "bottom-right",
          autoClose: 3000,
        });

        const signedAtTimestamp = response.data.data?.signedAt || new Date().toISOString();

        setSessionFormData(prev => ({
          ...prev,
          isSigned: true,
          signedAt: signedAtTimestamp
        }));
        setIsSigned(true);
        setIsSignedFromDB(true);
        setIsSubmit(true);

        try {
          await axios.put(
            `${process.env.REACT_APP_BASE_URL}/appointment/update-status/${session._id}`,
            { status: "Completed" },
            getConfigWithToken(user.user?.token)
          );
        } catch (statusError) {
          console.error("❌ Error updating appointment status:", statusError);
        }
      } else {
        toast.error(response.data.message || "Failed to sign and lock note.");
      }
    } catch (error) {
      console.error("Error signing and locking note:", error);
      toast.error(
        error.response?.data?.message ||
        "An error occurred while signing and locking note."
      );
    } finally {
      setSigningAndSubmitting(false);
    }
  };

  const handleDownload = async () => {
    if (!session?._id) {
      toast.error("Session ID is missing");
      return;
    }

    try {
      const clientName = `${session.user?.firstname || ""} ${session.user?.lastname || ""}`.trim();
      const providerName = `${session.health_specialist?.firstname || ""} ${session.health_specialist?.lastname || ""}`.trim();
      const appointmentDate = session.appointmentdate || session.appointmenttime;
      const getSignedDateFromDB = () => {
        if (!sessionFormData?.signedAt) return null;
        return new Date(sessionFormData.signedAt).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric"
        });
      };

      const getSignedTimeFromDB = () => {
        if (!sessionFormData?.signedAt) return null;
        return new Date(sessionFormData.signedAt).toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true
        });
      };

      // Format time function
      const formatDateTime = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      };

      const appointmentTime = session.appointmenttime ? formatDateTime(session.appointmenttime) : "N/A";

      // Fetch addendumData from database
      let addendumDataArray = [];
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/sessionHistory/get-by-session/${session._id}`,
          getConfigWithToken(user?.user?.token)
        );
        if (response.data.success && response.data.data?.addendumData) {
          addendumDataArray = Array.isArray(response.data.data.addendumData)
            ? response.data.data.addendumData
            : [];
        }
      } catch (error) {
        console.error("Error fetching addendum data:", error);
        // If error, use empty array
        addendumDataArray = [];
      }

      // Get generatePDF function from the PDF generator component
      const generatePDF = SessionHistoryPDFGenerator({
        sessionFormData,
        clientName,
        providerName,
        providerCredentials: formatCredentials(session?.health_specialist?.credentials) || "LPC",
        appointmentDate,
        appointmentTime,
        isSigned: isSigned || sessionFormData.isSigned,
        signedDateFromDB: getSignedDateFromDB(),
        signedTimeFromDB: getSignedTimeFromDB(),
        formatDate,
        formatDateTime,
        totalDuration: sessionFormData.totalDuration || derivedDurationMinutes,
        addendumData: addendumDataArray,
      });

      generatePDF();
    } catch (error) {
      console.error("Error downloading session:", error);
      toast.error("An error occurred while downloading the session.");
    }
  };

  const handleAddAddendum = () => {
    setAddAddendum(true);
  };

  // Check if latest addendum entry can be edited (addendumSigned: false, addendumSubmit: true)
  const canEditAddendum = () => {
    if (!addendumData || addendumData.length === 0) {
      return false;
    }
    const latestIndex = addendumData.length - 1;
    const latestAddendum = addendumData[latestIndex];
    return latestAddendum &&
      latestAddendum.addendumSigned === false &&
      latestAddendum.addendumSubmit === true;
  };

  // Handle Edit Addendum button click
  const handleEditAddendum = () => {
    if (!addendumData || addendumData.length === 0) {
      return;
    }
    const latestIndex = addendumData.length - 1;
    const latestAddendum = addendumData[latestIndex];

    // Set addendumSubmit to false in the latest object
    if (latestAddendum) {
      const updatedAddendumData = [...addendumData];
      updatedAddendumData[latestIndex] = {
        ...updatedAddendumData[latestIndex],
        addendumSubmit: false
      };
      setAddendumData(updatedAddendumData);

      if (latestAddendum.addendumText) {
        setSessionFormData(prev => ({ ...prev, addendumText: latestAddendum.addendumText || "" }));
        setCurrentAddendumIndex(latestIndex);
        setAddAddendum(true);
        addendumInitializedRef.current = false; // Reset initialization to load the text
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const derivedDurationMinutes = useMemo(() => {
    const startMinutes = getMinutesFromTimeString(sessionFormData.timeIn);
    const endMinutes = getMinutesFromTimeString(sessionFormData.timeOut);

    if (startMinutes === null || endMinutes === null) {
      return null;
    }

    let diff = endMinutes - startMinutes;
    if (diff < 0) {
      diff += 24 * 60;
    }
    return diff;
  }, [sessionFormData.timeIn, sessionFormData.timeOut, getMinutesFromTimeString]);

  const durationDisplay =
    derivedDurationMinutes !== null
      ? `${derivedDurationMinutes}m`
      : "";

  const sessionFormDataForDetailView = useMemo(
    () => ({
      ...sessionFormData,
      totalDuration:
        derivedDurationMinutes !== null
          ? derivedDurationMinutes
          : sessionFormData.totalDuration ?? null,
    }),
    [sessionFormData, derivedDurationMinutes]
  );

  if (!session) {
    return (
      <div className={styles.loadingContainer}>
        <p>Loading session data...</p>
      </div>
    );
  }

  if (loading || !dataLoaded) {
    return (
      <div className={styles.loadingContainer}>
        <p>Loading session history...</p>
      </div>
    );
  }

  const clientName = `${session.user?.firstname || ""} ${session.user?.lastname || ""}`.trim();
  const providerName = `${session.health_specialist?.firstname || ""} ${session.health_specialist?.lastname || ""}`.trim();
  const appointmentDate = session.appointmentdate || session.appointmenttime;


  const appointmentTime = session.appointmenttime ? formatDateTime(session.appointmenttime) : "N/A";

  // Get provider credentials - you may need to adjust this based on your data structure
  const providerCredentials = formatCredentials(session?.health_specialist?.credentials) || "LPC";

  // Get signed date/time (with fallback to current date/time for preview)
  const signedDate = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric"
  });

  const signedTime = new Date().toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });

  // Get signed date/time from database only (for display after signing)
  const getSignedDateFromDB = () => {
    if (!sessionFormData?.signedAt) return null;
    return new Date(sessionFormData.signedAt).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  };

  const getSignedTimeFromDB = () => {
    if (!sessionFormData?.signedAt) return null;
    return new Date(sessionFormData.signedAt).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  };

  const signedDateFromDB = getSignedDateFromDB();
  const signedTimeFromDB = getSignedTimeFromDB();

  const sections = [
    { id: "appointment-details", label: "Appointment Details" },
    { id: "presenting-concerns", label: "Presenting Concerns" },
    { id: "history-concerns", label: "History of Presenting Concerns" },
    { id: "medical-psychiatric", label: "Medical & Psychiatric History" },
    { id: "substance-use", label: "Substance Use" },
    { id: "family-social", label: "Family & Social History" },
    { id: "education-occupation", label: "Education & Occupation" },
    { id: "legal-history", label: "Legal History" },
    { id: "mental-status", label: "Mental Status Exam" },
    { id: "risk-safety", label: "Risk & Safety" },
    { id: "strength-resources", label: "Strength & Resources" },
    { id: "diagnosis", label: "Diagnosis" },
    { id: "care-plan", label: "Care Plan" },
  ];

  if (addAddendum === true || canEditAddendum() || (addendumData && Array.isArray(addendumData) && addendumData.length > 0)) {
    sections.push({
      id: "add-addendum",
      label: "Addendum / Correction"
    });
  }

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (isSubmit && typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("session-detail-view-expand", {
          detail: { sectionId, behavior: "toggle" },
        })
      );
    }
  };

  const renderAppointmentDetails = () => {
    return (
      <AppointmentDetailsSection
        clientName={clientName}
        providerName={providerName}
        appointmentDate={appointmentDate}
        appointmentTime={appointmentTime}
        durationDisplay={durationDisplay}
        sessionFormData={sessionFormData}
        updateFormData={updateFormData}
        timeInParts={timeInParts}
        timeOutParts={timeOutParts}
        isTimeInPickerOpen={isTimeInPickerOpen}
        isTimeOutPickerOpen={isTimeOutPickerOpen}
        timeInPickerRef={timeInPickerRef}
        timeOutPickerRef={timeOutPickerRef}
        handleTimeInputFocus={handleTimeInputFocus}
        setIsTimeInPickerOpen={setIsTimeInPickerOpen}
        setIsTimeOutPickerOpen={setIsTimeOutPickerOpen}
        handleTimeSegmentChange={handleTimeSegmentChange}
        setTimeInParts={setTimeInParts}
        setTimeOutParts={setTimeOutParts}
        handleTimeSegmentBlur={handleTimeSegmentBlur}
        handleHourAdjust={handleHourAdjust}
        handleMinuteAdjust={handleMinuteAdjust}
        handlePeriodToggle={handlePeriodToggle}
        formatDate={formatDate}
      />
    );
  };

  const handlePrimaryDiagnosisSelect = (diagnosis) => {
    if (!diagnosis) return;
    updateFormData("primaryDiagnosis", diagnosis.code);
    updateFormData("primaryDiagnosisDescription", diagnosis.description);
    setPrimaryDiagnosisError(""); // Clear error when diagnosis is selected
  };

  const handlePrimaryDiagnosisClear = () => {
    updateFormData("primaryDiagnosis", "");
    updateFormData("primaryDiagnosisDescription", "");
    setPrimaryDiagnosisSearchTerm("");
    setPrimaryDiagnosisResults([]);
  };

  const togglePrimaryDiagnosisDropdown = () => {
    setIsPrimaryDiagnosisDropdownOpen((prev) => !prev);
  };

  const handleSecondaryDiagnosisSelect = (diagnosis) => {
    if (!diagnosis) return;
    updateFormData("secondaryDiagnosis", diagnosis.code);
    updateFormData("secondaryDiagnosisDescription", diagnosis.description);
  };

  const toggleSecondaryDiagnosisDropdown = () => {
    setIsSecondaryDiagnosisDropdownOpen((prev) => !prev);
  };

  const handleTertiaryDiagnosisSelect = (diagnosis) => {
    if (!diagnosis) return;
    updateFormData("tertiaryDiagnosis", diagnosis.code);
    updateFormData("tertiaryDiagnosisDescription", diagnosis.description);
  };

  const handleTertiaryDiagnosisClear = () => {
    updateFormData("tertiaryDiagnosis", "");
    updateFormData("tertiaryDiagnosisDescription", "");
    setTertiaryDiagnosisSearchTerm("");
    setTertiaryDiagnosisResults([]);
  };

  const toggleTertiaryDiagnosisDropdown = () => {
    setIsTertiaryDiagnosisDropdownOpen((prev) => !prev);
  };

  const handleConcernChange = (item) => {
    const currentConcerns = sessionFormData.selectedConcerns;
    if (currentConcerns.includes(item)) {
      updateFormData("selectedConcerns", currentConcerns.filter((concern) => concern !== item));
      if (item === "Other") {
        updateFormData("otherConcernText", "");
      }
    } else {
      updateFormData("selectedConcerns", [...currentConcerns, item]);
    }
    if (checkboxError) {
      setCheckboxError("");
    }
  };

  const handleMedicalConditionChange = (item) => {
    const currentConditions = sessionFormData.selectedMedicalConditions;

    if (item === "None") {
      if (!currentConditions.includes("None")) {
        updateFormData("selectedMedicalConditions", ["None"]);
        updateFormData("otherMedicalConditionText", "");
      } else {
        updateFormData("selectedMedicalConditions", currentConditions.filter((condition) => condition !== item));
      }
    } else {
      if (currentConditions.includes(item)) {
        const newConditions = currentConditions.filter((condition) => condition !== item);
        updateFormData("selectedMedicalConditions", newConditions);
        if (item === "Other") {
          updateFormData("otherMedicalConditionText", "");
        }
      } else {
        const newConditions = currentConditions.filter((condition) => condition !== "None");
        updateFormData("selectedMedicalConditions", [...newConditions, item]);
      }
    }

    // Clear error when checkbox is selected
    if (medicalConditionsError) {
      setMedicalConditionsError("");
    }
  };

  const handlePastPsychiatricChange = (item) => {
    const currentPastPsychiatric = sessionFormData.selectedPastPsychiatric;

    if (item === "None") {
      if (!currentPastPsychiatric.includes("None")) {
        updateFormData("selectedPastPsychiatric", ["None"]);
        updateFormData("otherPastPsychiatricText", "");
      } else {
        updateFormData("selectedPastPsychiatric", currentPastPsychiatric.filter((treatment) => treatment !== item));
      }
    } else {
      if (currentPastPsychiatric.includes(item)) {
        const newTreatments = currentPastPsychiatric.filter((treatment) => treatment !== item);
        updateFormData("selectedPastPsychiatric", newTreatments);
        if (item === "Other") {
          updateFormData("otherPastPsychiatricText", "");
        }
      } else {
        const newTreatments = currentPastPsychiatric.filter((treatment) => treatment !== "None");
        updateFormData("selectedPastPsychiatric", [...newTreatments, item]);
      }
    }
  };

  const handleFamilyPsychiatricChange = (item) => {
    const currentFamilyPsychiatric = sessionFormData.selectedFamilyPsychiatric;

    if (item === "None") {
      if (!currentFamilyPsychiatric.includes("None")) {
        updateFormData("selectedFamilyPsychiatric", ["None"]);
        updateFormData("otherFamilyPsychiatricText", "");
      } else {
        updateFormData("selectedFamilyPsychiatric", currentFamilyPsychiatric.filter((condition) => condition !== item));
      }
    } else {
      if (currentFamilyPsychiatric.includes(item)) {
        const newConditions = currentFamilyPsychiatric.filter((condition) => condition !== item);
        updateFormData("selectedFamilyPsychiatric", newConditions);
        if (item === "Other") {
          updateFormData("otherFamilyPsychiatricText", "");
        }
      } else {
        const newConditions = currentFamilyPsychiatric.filter((condition) => condition !== "None");
        updateFormData("selectedFamilyPsychiatric", [...newConditions, item]);
      }
    }
  };

  const handleSupportSystemChange = (item) => {
    const currentSupportSystem = sessionFormData.selectedSupportSystem;

    if (item === "No consistent supports") {
      if (!currentSupportSystem.includes("No consistent supports")) {
        updateFormData("selectedSupportSystem", ["No consistent supports"]);
        updateFormData("otherSupportSystemText", "");
      } else {
        updateFormData("selectedSupportSystem", currentSupportSystem.filter((support) => support !== item));
      }
    } else {
      if (currentSupportSystem.includes(item)) {
        const newSupports = currentSupportSystem.filter((support) => support !== item);
        updateFormData("selectedSupportSystem", newSupports);
        if (item === "Other") {
          updateFormData("otherSupportSystemText", "");
        }
      } else {
        const newSupports = currentSupportSystem.filter((support) => support !== "No consistent supports");
        updateFormData("selectedSupportSystem", [...newSupports, item]);
      }
    }

    if (supportSystemError) {
      setSupportSystemError("");
    }
  };

  const handleStrengthsChange = (item) => {
    const currentStrengths = sessionFormData.selectedStrengths;
    if (currentStrengths.includes(item)) {
      updateFormData("selectedStrengths", currentStrengths.filter((strength) => strength !== item));
      if (item === "Other") {
        updateFormData("otherStrengthsText", "");
      }
    } else {
      updateFormData("selectedStrengths", [...currentStrengths, item]);
    }
    if (strengthsError) {
      setStrengthsError("");
    }
  };

  const renderPresentingConcerns = () => {
    return (
      <PresentingConcernsSection
        sessionFormData={sessionFormData}
        updateFormData={updateFormData}
        handleConcernChange={handleConcernChange}
        checkboxError={checkboxError}
        autoResizeTextarea={autoResizeTextarea}
      />
    );
  };

  const renderHistoryConcerns = () => {
    return (
      <HistoryConcernsSection
        sessionFormData={sessionFormData}
        updateFormData={updateFormData}
        durationError={durationError}
        frequencyError={frequencyError}
        severityError={severityError}
        setDurationError={setDurationError}
        setFrequencyError={setFrequencyError}
        setSeverityError={setSeverityError}
      />
    );
  };

  const renderMedicalConcerns = () => {
    return (
      <MedicalConcernsSection
        sessionFormData={sessionFormData}
        updateFormData={updateFormData}
        handleMedicalConditionChange={handleMedicalConditionChange}
        handlePastPsychiatricChange={handlePastPsychiatricChange}
        handleFamilyPsychiatricChange={handleFamilyPsychiatricChange}
        medicalConditionsError={medicalConditionsError}
        autoResizeTextarea={autoResizeTextarea}
      />
    );
  };

  const renderSubstanceConcerns = () => {
    return (
      <SubstanceConcernsSection
        sessionFormData={sessionFormData}
        updateFormData={updateFormData}
        alcoholError={alcoholError}
        cannabisError={cannabisError}
        tobaccoError={tobaccoError}
        illicitDrugsError={illicitDrugsError}
        setAlcoholError={setAlcoholError}
        setCannabisError={setCannabisError}
        setTobaccoError={setTobaccoError}
        setIllicitDrugsError={setIllicitDrugsError}
        handleSubstanceTextareaChange={handleSubstanceTextareaChange}
      />
    );
  };

  const renderFamilyConcerns = () => {
    return (
      <FamilyConcernsSection
        sessionFormData={sessionFormData}
        updateFormData={updateFormData}
        handleSupportSystemChange={handleSupportSystemChange}
        livingSituationError={livingSituationError}
        supportSystemError={supportSystemError}
        setLivingSituationError={setLivingSituationError}
        setSupportSystemError={setSupportSystemError}
        autoResizeTextarea={autoResizeTextarea}
      />
    );
  };

  const renderEducationConcerns = () => {
    return (
      <EducationConcernsSection
        sessionFormData={sessionFormData}
        updateFormData={updateFormData}
        educationLevelError={educationLevelError}
        employmentStatusError={employmentStatusError}
        setEducationLevelError={setEducationLevelError}
        setEmploymentStatusError={setEmploymentStatusError}
        autoResizeTextarea={autoResizeTextarea}
      />
    );
  };

  const renderMentalStatusConcerns = () => {
    return (
      <MentalStatusConcernsSection
        sessionFormData={sessionFormData}
        updateFormData={updateFormData}
        appearanceError={appearanceError}
        behaviorError={behaviorError}
        speechError={speechError}
        moodError={moodError}
        affectError={affectError}
        thoughtProcessError={thoughtProcessError}
        orientationError={orientationError}
        insightJudgmentError={insightJudgmentError}
        setAppearanceError={setAppearanceError}
        setBehaviorError={setBehaviorError}
        setSpeechError={setSpeechError}
        setMoodError={setMoodError}
        setAffectError={setAffectError}
        setThoughtProcessError={setThoughtProcessError}
        setOrientationError={setOrientationError}
        setInsightJudgmentError={setInsightJudgmentError}
      />
    );
  };

  const renderRiskSafetyConcerns = () => {
    return (
      <RiskSafetyConcernsSection
        sessionFormData={sessionFormData}
        updateFormData={updateFormData}
        suicideRiskError={suicideRiskError}
        homicideRiskError={homicideRiskError}
        abuseNeglectError={abuseNeglectError}
        safetyPlanNotesError={safetyPlanNotesError}
        setSuicideRiskError={setSuicideRiskError}
        setHomicideRiskError={setHomicideRiskError}
        setAbuseNeglectError={setAbuseNeglectError}
        setSafetyPlanNotesError={setSafetyPlanNotesError}
      />
    );
  };

  const renderStrengthConcerns = () => {
    return (
      <StrengthConcernsSection
        sessionFormData={sessionFormData}
        updateFormData={updateFormData}
        handleStrengthsChange={handleStrengthsChange}
        strengthsError={strengthsError}
        autoResizeTextarea={autoResizeTextarea}
      />
    );
  };

  const renderDiagnosisConcerns = () => {
    return (
      <DiagnosisConcernsSection
        sessionFormData={sessionFormData}
        primaryDiagnosisError={primaryDiagnosisError}
        diagnosisCodeRef={diagnosisCodeRef}
        secondaryDiagnosisCodeRef={secondaryDiagnosisCodeRef}
        tertiaryDiagnosisCodeRef={tertiaryDiagnosisCodeRef}
        isPrimaryDiagnosisDropdownOpen={isPrimaryDiagnosisDropdownOpen}
        isSecondaryDiagnosisDropdownOpen={isSecondaryDiagnosisDropdownOpen}
        isTertiaryDiagnosisDropdownOpen={isTertiaryDiagnosisDropdownOpen}
        togglePrimaryDiagnosisDropdown={togglePrimaryDiagnosisDropdown}
        toggleSecondaryDiagnosisDropdown={toggleSecondaryDiagnosisDropdown}
        toggleTertiaryDiagnosisDropdown={toggleTertiaryDiagnosisDropdown}
        primaryDiagnosisSearchTerm={primaryDiagnosisSearchTerm}
        secondaryDiagnosisSearchTerm={secondaryDiagnosisSearchTerm}
        tertiaryDiagnosisSearchTerm={tertiaryDiagnosisSearchTerm}
        setPrimaryDiagnosisSearchTerm={setPrimaryDiagnosisSearchTerm}
        setSecondaryDiagnosisSearchTerm={setSecondaryDiagnosisSearchTerm}
        setTertiaryDiagnosisSearchTerm={setTertiaryDiagnosisSearchTerm}
        diagnosisSearchLoading={diagnosisSearchLoading}
        secondaryDiagnosisSearchLoading={secondaryDiagnosisSearchLoading}
        tertiaryDiagnosisSearchLoading={tertiaryDiagnosisSearchLoading}
        diagnosisSearchError={diagnosisSearchError}
        secondaryDiagnosisSearchError={secondaryDiagnosisSearchError}
        tertiaryDiagnosisSearchError={tertiaryDiagnosisSearchError}
        primaryDiagnosisResults={primaryDiagnosisResults}
        secondaryDiagnosisResults={secondaryDiagnosisResults}
        tertiaryDiagnosisResults={tertiaryDiagnosisResults}
        handlePrimaryDiagnosisSelect={handlePrimaryDiagnosisSelect}
        handleSecondaryDiagnosisSelect={handleSecondaryDiagnosisSelect}
        handleTertiaryDiagnosisSelect={handleTertiaryDiagnosisSelect}
        setIsPrimaryDiagnosisDropdownOpen={setIsPrimaryDiagnosisDropdownOpen}
        setIsSecondaryDiagnosisDropdownOpen={setIsSecondaryDiagnosisDropdownOpen}
        setIsTertiaryDiagnosisDropdownOpen={setIsTertiaryDiagnosisDropdownOpen}
      />
    );
  };

  const renderCarePlanConcerns = () => {
    return (
      <CarePlanConcernsSection
        sessionFormData={sessionFormData}
        updateFormData={updateFormData}
        clientGoalError={clientGoalError}
        objectivesError={objectivesError}
        interventionsError={interventionsError}
        sessionFrequencyError={sessionFrequencyError}
        reviewDateError={reviewDateError}
        setClientGoalError={setClientGoalError}
        setObjectivesError={setObjectivesError}
        setInterventionsError={setInterventionsError}
        setSessionFrequencyError={setSessionFrequencyError}
        setReviewDateError={setReviewDateError}
        autoResizeTextarea={autoResizeTextarea}
      />
    );
  };

  const renderAllSections = () => {
    return (
      <>
        {renderAppointmentDetails()}
        {renderPresentingConcerns()}
        {renderHistoryConcerns()}
        {renderMedicalConcerns()}
        {renderSubstanceConcerns()}
        {renderFamilyConcerns()}
        {renderEducationConcerns()}
        {/* {renderLegalHistoryConcerns()} */}
        {renderMentalStatusConcerns()}
        {renderRiskSafetyConcerns()}
        {renderStrengthConcerns()}
        {renderDiagnosisConcerns()}
        {renderCarePlanConcerns()}
      </>
    );
  };

  // Function to calculate addendum number based on addendumData
  const getAddendumNumber = () => {
    // Agar addendumData array empty hai to "Addendum # 1" show karo
    if (!addendumData || addendumData.length === 0) {
      return "Addendum # 1";
    }

    // Agar array mein objects hain to last object ko check karo
    const lastAddendum = addendumData[addendumData.length - 1];
    const arrayLength = addendumData.length;

    // Agar latest object ka addendumSigned false hai aur addendumSubmit true hai to length show karo
    if (lastAddendum && lastAddendum.addendumSigned === false && lastAddendum.addendumSubmit === true) {
      return `Addendum # ${arrayLength}`;
    }

    // Agar last object ka addendumSigned true hai to length + 1 show karo
    if (lastAddendum && lastAddendum.addendumSigned === true) {
      return `Addendum # ${arrayLength + 1}`;
    }

    // Agar last object ka addendumSigned false hai to sirf length show karo
    return `Addendum # ${arrayLength}`;
  };

  return (
    <div className={styles.container}>
      <ToastContainer position="top-right" />
      {/* Header */}
      <div className={styles.headerContainer}>
        {/* Initial Assessment Card */}
        <Card className={styles.headerCard}>
          <span className={styles.headerTitle}>
            {(() => {
              // Agar addAddendum true hai to addendum number show karo
              if (addAddendum) {
                return getAddendumNumber();
              }

              // Agar addAddendum false hai to check karo latest addendumData entry
              if (addendumData && addendumData.length > 0) {
                const latestAddendum = addendumData[addendumData.length - 1];
                // Agar latest entry ka addendumSigned false hai aur addendumSubmit true hai to addendum number show karo
                if (latestAddendum && latestAddendum.addendumSigned === false && latestAddendum.addendumSubmit === true) {
                  return getAddendumNumber();
                }
              }

              // Warna Initial Assessment show karo
              return "Initial Assessment";
            })()}
          </span>

          <span className={styles.headerSubtitle}>
            {clientName} · {appointmentDate ? formatDateOnly(appointmentDate) : "N/A"}, {appointmentTime}
          </span>
        </Card>

        <SessionActionButtons
          onBack={handleBackNavigation}
          isSigned={isSigned}
          addAddendum={addAddendum}
          isSubmit={isSubmit}
          saving={saving}
          signingAndSubmitting={signingAndSubmitting}
          saveStatus={saveStatus}
          canEditAddendum={canEditAddendum()}
          onSave={handleSave}
          onEdit={handleEdit}
          onAddAddendum={handleAddAddendum}
          onEditAddendum={handleEditAddendum}
          onDownload={handleDownload}
          onAddendumSubmit={handleAddendumSubmit}
          onSignAndSubmit={handleSignAndSubmit}
          editExistingAddendum={editExistingAddendum}
        />
      </div>

      <div className={styles.contentContainer}>
        <Card className={styles.sidebarCard}>
          <div className={styles.sidebarContainer}>
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={styles.sidebarButton}
                onMouseEnter={(e) => {
                  if (activeSection !== section.id) {
                    e.target.style.backgroundColor = "#f8f8f8";
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSection !== section.id) {
                    e.target.style.backgroundColor = "transparent";
                  }
                }}>
                {section.label}
              </button>
            ))}
          </div>
        </Card>

        <div className={styles.contentWrapper}>
          {isSubmit && (addAddendum || canEditAddendum()) ? (
            <AddendumView
              addendumText={sessionFormData.addendumText}
              onAddendumTextChange={(value) => {
                updateFormData("addendumText", value);
                // Clear error when user starts typing
                if (addendumTextError) {
                  setAddendumTextError("");
                }
              }}
              autoResizeTextarea={autoResizeTextarea}
              addendumNumber={getAddendumNumber()}
              addendumTextError={addendumTextError}
              canEditAddendum={canEditAddendum()}
              addendumData={addendumData}
              session={session}
              sessionFormData={sessionFormDataForDetailView}
            />
          ) : isSubmit ? (
            <SessionDetailedViewPage
              sessionFormData={sessionFormDataForDetailView}
              session={session}
              addendumData={addendumData}
            />
          ) : (
            renderAllSections()
          )}
        </div>

        <div style={{
          width: "29%",
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: "24px"
        }}>
          {isSubmit && (!addAddendum || (editExistingAddendum && canEditAddendum())) && (
            <SignAndLockCard
              providerName={providerName}
              providerCredentials={providerCredentials}
              isSigned={isSigned}
              signingAndSubmitting={signingAndSubmitting}
              onSignAndLock={handleSignAndLock}
              signedAt={sessionFormData?.signedAt}
              canEditAddendum={canEditAddendum()}
            />
          )}

          {isSubmit && !addAddendum && addendumData && Array.isArray(addendumData) && addendumData.length > 0 && !canEditAddendum() && (
            <>
              {addendumData.map((addendum, index) => {
                if (addendum.addendumSigned === true && addendum.addendumSignedAt) {
                  const addendumNumber = index + 1;
                  const addendumSignedDate = new Date(addendum.addendumSignedAt).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric"
                  });
                  const addendumSignedTime = new Date(addendum.addendumSignedAt).toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true
                  });

                  return (
                    <Card key={`addendum-signature-${index}`} className={styles.card} style={{
                      width: "100%",
                      marginBottom: "0"
                    }}>
                      <h3 className={styles.sectionHeading} style={{ marginBottom: "24px", fontSize: "20px" }}>
                        Provider Signature - Addendum # {addendumNumber}
                      </h3>

                      <div className={styles.separator} style={{ width: "40%" }}></div>

                      <div style={{ marginBottom: "24px" }}>
                        <div style={{
                          padding: "20px",
                          border: "none !important",
                          borderRadius: "4px",
                          backgroundColor: "#ECF8FE",
                          minHeight: "150px",
                          textAlign: "left"
                        }}>
                          <div style={{
                            fontFamily: "'Brush Script MT', 'Brush Script', cursive",
                            fontSize: "32px",
                            color: "#1a1a1a",
                            marginBottom: "16px",
                            textAlign: "left",
                            fontWeight: "400",
                            letterSpacing: "1px"
                          }}>
                            {providerName || ""}
                          </div>
                          <div style={{
                            fontSize: "14px",
                            color: "#666",
                            marginBottom: "4px"
                          }}>
                            Signed by {providerName || ""}, {providerCredentials || ""}
                          </div>
                          <div style={{
                            fontSize: "14px",
                            color: "#666"
                          }}>
                            {addendumSignedDate}; {addendumSignedTime}
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                }
                return null;
              })}
            </>
          )}
        </div>

      </div>

    </div>
  );
};

export default SessionHistoryDetailView;